<template>
  <div :style="styleVars" class="h-full bg-muted">
    <div class="flex flex-col items-center justify-center w-full pb-20 bg-muted">
      <section class="flex flex-col items-center w-full justify-center">
        <!-- Header Section -->
        <div v-if="isLoading" class="flex items-center justify-between md:mt-5 mb-2 md:mb-0 mt-2 px-2 md:px-0 w-full header-bar">
          <!-- Skeleton Left Side: Page Title -->
          <div class="items-center gap-2 px-4 -ml-2 d-flex">
            <div class="skeleton-logo"></div>
            <div class="flex items-center gap-1">
              <div class="skeleton-brand-name"></div>
              <div class="skeleton-location-count"></div>
            </div>
          </div>

          <!-- Skeleton Header Tabs -->
          <div class="header-tabs flex gap-2 px-4 py-2">
            <div class="skeleton-tab"></div>
            <div class="skeleton-tab"></div>
            <div class="skeleton-tab"></div>
          </div>

          <!-- Skeleton Right Side: Search -->
          <div class="flex items-center ml-auto justify-end gap-2 mr-0 md:mr-6" style="max-width: 100%; min-width: 0;">
            <div class="skeleton-search"></div>
          </div>
        </div>

        <div v-else class="flex items-center justify-between md:mt-5 mb-2 md:mb-0 mt-2 px-2 md:px-0 w-full header-bar">
          <!-- Left Side: Page Title -->
          <div class="items-center gap-2 px-4 -ml-2 d-flex">
            <img
              v-if="storeLogo && storeLogo !== 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNMTIgMkM2LjQ4IDIgMiA2LjQ4IDIgMTJzNC40OCAxMCAxMCAxMCAxMC00LjQ4IDEwLTEwUzE3LjUyIDIgMTIgMnptMCAxOGMtNC40MSAwLTgtMy41OS04LThzMy41OS04IDgtOCA4IDMuNTkgOCA4LTMuNTkgOC04IDh6IiBmaWxsPSIjZTVlN2ViIi8+PC9zdmc+'"
              :src="storeLogo"
              class="cursor-pointer object-contain dynamic-logo"
              :alt="brandName"
              @error="handleLogoError"
              @load="handleLogoLoad"
              @loadstart="() => console.log('🔄 [BRAND LOGO] Header logo loading started:', storeLogo)"
            />
            <v-icon
              v-else
              class="cursor-pointer dynamic-logo"
              color="primary"
              :size="logoIconSize"
            >
              mdi-store
            </v-icon>
            <div class="flex items-center gap-1">
              <p class="brand-name-text font-bold">{{ brandName }}</p>
              <span class="text-sm ml-2" v-if="totalLocations > 0" style="color: var(--store-brand-name-color);">
                ({{ totalLocations }} locations)
              </span>
            </div>
          </div>

          <!-- Header Tabs -->
          <div v-if="headerTabs.length" class="header-tabs flex gap-2 px-4 py-2">
            <button
              v-for="tab in headerTabs"
              :key="tab.value"
              :class="['header-tab', { 'header-tab-selected': selectedTab === tab.value }]"
              @click="selectHeaderTab(tab.value)"
            >
              {{ tab.label }}
            </button>
          </div>

          <!-- Right Side: Search -->
          <div class="flex items-center ml-auto justify-end gap-2 mr-0 md:mr-6" style="max-width: 100%; min-width: 0;">
            <div class="relative w-full md:w-80 max-w-full">
              <v-text-field
                v-model="searchQuery"
                placeholder="Search locations..."
                prepend-inner-icon="mdi-magnify"
                outlined
                dense
                hide-details
                class="search-input w-full md:w-80"
              />
            </div>
          </div>
        </div>

        <!-- Main Content -->
        <div class="w-full bg-white rounded-lg shadow-md">
          <!-- Skeleton Loader for Main Content -->
          <div v-if="isLoading" class="grid grid-cols-1 lg:grid-cols-3 gap-0 min-h-screen">
            <!-- Skeleton Tree View Panel -->
            <div class="lg:col-span-1 border-r border-gray-200">
              <!-- Skeleton City Filter -->
              <div class="city-filter-container mb-4 px-4">
                <div class="skeleton-label mb-1"></div>
                <div class="skeleton-dropdown"></div>
              </div>
              <div class="p-4 side-panel-tree-view h-full">
                <!-- Skeleton Tree Items -->
                <div class="space-y-4">
                  <!-- Country Level Skeleton -->
                  <div class="skeleton-tree-item">
                    <div class="skeleton-chevron"></div>
                    <div class="skeleton-icon"></div>
                    <div class="skeleton-text flex-1"></div>
                    <div class="skeleton-count"></div>
                  </div>
                  
                  <!-- State Level Skeleton -->
                  <div class="ml-6 space-y-3">
                    <div class="skeleton-tree-item">
                      <div class="skeleton-chevron"></div>
                      <div class="skeleton-icon"></div>
                      <div class="skeleton-text flex-1"></div>
                      <div class="skeleton-count"></div>
                    </div>
                    
                    <!-- Location Level Skeleton -->
                    <div class="ml-6 space-y-2">
                      <div class="skeleton-location-item"></div>
                      <div class="skeleton-location-item"></div>
                      <div class="skeleton-location-item"></div>
                    </div>
                  </div>
                  
                  <!-- More Country Items -->
                  <div class="skeleton-tree-item">
                    <div class="skeleton-chevron"></div>
                    <div class="skeleton-icon"></div>
                    <div class="skeleton-text flex-1"></div>
                    <div class="skeleton-count"></div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Skeleton Details Panel -->
            <div class="lg:col-span-2">
              <div class="p-6 h-full">
                <!-- Skeleton Cover Photo -->
                <div class="skeleton-cover-photo mb-6"></div>
                
                <!-- Skeleton Details Header -->
                <div class="skeleton-details-header mb-6"></div>
                
                <!-- Skeleton Content Sections -->
                <div class="space-y-6">
                  <!-- Skeleton Address Panel -->
                  <div class="skeleton-panel">
                    <div class="skeleton-panel-header"></div>
                    <div class="skeleton-panel-content">
                      <div class="skeleton-text-long"></div>
                      <div class="skeleton-button"></div>
                    </div>
                    <div class="skeleton-map"></div>
                  </div>
                  
                  <!-- Skeleton Contact Panel -->
                  <div class="skeleton-panel">
                    <div class="skeleton-panel-header"></div>
                    <div class="skeleton-panel-content">
                      <div class="space-y-4">
                        <div class="skeleton-contact-item"></div>
                        <div class="skeleton-contact-item"></div>
                        <div class="skeleton-contact-item"></div>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Skeleton Description Panel -->
                  <div class="skeleton-panel">
                    <div class="skeleton-panel-header"></div>
                    <div class="skeleton-panel-content">
                      <div class="skeleton-text-long"></div>
                      <div class="skeleton-text-medium"></div>
                    </div>
                  </div>
                  
                  <!-- Skeleton Reviews Panel -->
                  <div class="skeleton-panel">
                    <div class="skeleton-panel-header"></div>
                    <div class="skeleton-panel-content">
                      <div class="space-y-4">
                        <div class="skeleton-review-item"></div>
                        <div class="skeleton-review-item"></div>
                        <div class="skeleton-review-item"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Actual Content -->
          <div v-else class="grid grid-cols-1 lg:grid-cols-3 gap-0 min-h-screen">
            <!-- Tree View Panel -->
            <div class="lg:col-span-1 border-r border-gray-200">
              <!-- City Filter Dropdown -->
              <div
                class="city-filter-container mb-4 px-4"
                style="--city-filter-primary: #000;"
              >
                <label for="city-filter" class="block text-sm font-semibold mb-1 city-filter-label">Filter by City:</label>
                <select
                  id="city-filter"
                  v-model="selectedCityFilter"
                  class="city-filter-dropdown w-full border rounded px-3 py-2 focus:outline-none"
                >
                  <option value="">All Cities</option>
                  <option v-for="city in uniqueCities" :key="city">{{ city }}</option>
                  <option value="__other__">Other Cities</option>
                </select>
              </div>
              <div class="p-4 side-panel-tree-view h-full">
                <div v-if="Object.keys(locationTree).length === 0" class="text-center py-8">
                  <v-icon large color="gray">mdi-map-marker-off</v-icon>
                  <p class="text-gray-500 mt-2">No locations found</p>
                </div>

                <div v-else>
                  <!-- Country Level -->
                  <div
                    v-for="(countryData, country) in filteredLocationTree"
                    :key="country"
                    class="mb-4"
                  >
                    <div
                      class="flex items-center gap-2 p-2 cursor-pointer hover:bg-gray-50 rounded"
                      @click="toggleCountry(country)"
                    >
                      <v-icon small :class="['chevron-icon', expandedCountries.includes(country) ? 'rotate-90' : '']">mdi-chevron-right</v-icon>
                      <v-icon v-if="showSidepanelIcons === 'all'" small>mdi-earth</v-icon>
                      <span class="font-semibold text-gray-700">{{ (country || 'Other Countries').charAt(0).toUpperCase() + (country || 'Other Countries').slice(1) }}</span>
                      <span class="text-xs text-gray-500 ml-auto">({{ countryData.totalCount }})</span>
                    </div>

                    <!-- State Level -->
                    <div v-show="expandedCountries.includes(country)" class="ml-6 mt-2">
                      <div
                        v-for="(stateData, state) in countryData.states"
                        :key="`${country}-${state}`"
                        class="mb-2"
                      >
                        <div
                          class="flex items-center gap-2 p-2 cursor-pointer hover:bg-gray-50 rounded"
                          @click="toggleState(country, state)"
                        >
                          <v-icon small :class="['chevron-icon', expandedStates.includes(`${country}-${state}`) ? 'rotate-90' : '']">mdi-chevron-right</v-icon>
                          <v-icon v-if="showSidepanelIcons === 'all'" small>mdi-map</v-icon>
                          <span class="font-medium text-gray-600">{{ state || 'Other States' }}</span>
                          <span class="text-xs text-gray-500 ml-auto">({{ stateData.locations.length }})</span>
                        </div>

                        <!-- Location Level -->
                        <div v-show="expandedStates.includes(`${country}-${state}`)" class="ml-6 mt-2">
                          <div
                            v-for="location in stateData.locations"
                            :key="location.id"
                            class="flex items-center gap-2 p-2 cursor-pointer rounded-xl transition-colors location-selector-item"
                            :class="{
                              'location-selected': selectedLocation?.id === location.id
                            }"
                            @click="selectLocation(location)"
                          >
                            <v-icon v-if="showSidepanelIcons === 'all'" small :color="location.iconColor || 'primary'">mdi-store</v-icon>
                            <div class="flex-1 min-w-0">
                              <p class="text-sm font-medium text-gray-800 truncate">
                                {{ location.name || location.storeLocation }}
                              </p>
                              <p class="text-xs text-gray-500 truncate">
                                {{ location.city }}{{ location.city && location.state ? ', ' : '' }}{{ location.state }}
                              </p>
                            </div>
                            <div class="flex-shrink-0">
                              <v-icon small class="chevron-icon">mdi-chevron-right</v-icon>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Details Panel -->
            <div class="lg:col-span-2">
              <div v-if="!selectedLocation" class="flex items-center justify-center h-full bg-gray-50">
                <div class="text-center">
                  <v-icon large color="gray">mdi-map-marker-outline</v-icon>
                  <p class="text-gray-500 mt-4 text-lg">Select a location to view details</p>
                  <p class="text-gray-400 text-sm">Choose a location from the tree view on the left</p>
                </div>
              </div>

              <div v-else>
                <div class="p-6 h-full">
                  <!-- Cover Photo at the very top -->
                  <div class="details-panel details-cover-photo mb-6" v-if="getCoverPhoto(selectedLocation) && !coverPhotoError">
                    <div class="cover-photo-container">
                      <div v-if="coverPhotoLoading" class="w-full h-64 bg-gray-200 rounded-lg shadow-md flex items-center justify-center">
                        <v-progress-circular indeterminate color="primary" size="32"></v-progress-circular>
                        <span class="ml-2 text-gray-500">Loading cover photo...</span>
                      </div>
                      <img 
                        v-show="!coverPhotoLoading"
                        :src="getCoverPhoto(selectedLocation)" 
                        :alt="`Cover photo for ${selectedLocation.name || selectedLocation.storeLocation}`"
                        class="w-full h-64 object-cover rounded-lg shadow-md"
                        @error="handleCoverPhotoError"
                        @load="handleCoverPhotoLoad"
                        @loadstart="coverPhotoLoading = true"
                      />
                    </div>
                  </div>

                  <!-- Details Header -->
                  <div class="details-header mb-6 shadow-lg p-8">
                    <div class="flex flex-row items-center justify-between w-full">
                      <div class="flex flex-col items-start flex-1 min-w-0">
                        <h2 class="text-2xl font-bold text-gray-800 mb-2 flex items-center">
                          <v-icon class="mr-2 details-header-icon">mdi-store</v-icon>
                          {{ selectedLocation.name || selectedLocation.storeLocation }}
                        </h2>
                        <div class="flex flex-col gap-2 text-lg font-medium text-gray-700 mb-2 w-full">
                          <div v-if="selectedLocation.averageRating" class="flex items-center gap-1">
                            <span class="text-lg font-semibold mr-2">{{ selectedLocation.averageRating.toFixed(1) }}</span>
                            <span class="text-gray-500 text-base mr-2" v-if="selectedLocation.reviewCount">
                              ({{ selectedLocation.reviewCount }})
                            </span>
                            <span class="star-rating flex items-center">
                              <v-icon
                                v-for="star in 5"
                                :key="'star-'+star"
                                :color="star <= fullStars ? 'amber' : (star === fullStars + 1 && decimalPart >= 0.5 ? 'amber' : 'grey-lighten-2')"
                                size="22"
                                class="star-icon"
                              >
                                {{ star <= fullStars ? 'mdi-star' : (star === fullStars + 1 && decimalPart >= 0.5 ? 'mdi-star-half-full' : 'mdi-star-outline') }}
                              </v-icon>
                            </span>
                          </div>
                          <div v-if="selectedLocation.gmbProfile && selectedLocation.gmbProfile.categories && selectedLocation.gmbProfile.categories.primaryCategory && selectedLocation.gmbProfile.categories.primaryCategory.displayName"
                               class="flex items-center gap-1">
                            <v-icon class="details-header-icon" size="20" :style="{ color: 'var(--details-primary)' }">mdi-tag</v-icon>
                            <span class="details-btn px-2 py-1 rounded-full text-sm font-semibold">
                              {{ selectedLocation.gmbProfile.categories.primaryCategory.displayName }}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div class="flex-shrink-0 ml-6">
                        <img
                          v-if="storeLogo && storeLogo !== 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNMTIgMkM2LjQ4IDIgMiA2LjQ4IDIgMTJzNC40OCAxMCAxMCAxMCAxMC00LjQ4IDEwLTEwUzE3LjUyIDIgMTIgMnptMCAxOGMtNC40MSAwLTgtMy41OS04LThzMy41OS04IDgtOCA4IDMuNTkgOCA4LTMuNTkgOC04IDh6IiBmaWxsPSIjZTVlN2ViIi8+PC9zdmc+'"
                          :src="storeLogo"
                          class="object-contain rounded-lg shadow-md details-header-logo"
                          :alt="brandName + ' logo'"
                          style="max-width: 120px; max-height: 120px;"
                          @error="handleLogoError"
                          @load="handleLogoLoad"
                          @loadstart="() => console.log('🔄 [BRAND LOGO] Details header logo loading started:', storeLogo)"
                        />
                        <v-icon
                          v-else
                          class="details-header-logo"
                          color="primary"
                          :size="logoIconSize"
                        >
                          mdi-store
                        </v-icon>
                      </div>
                    </div>
                  </div>

                  <div class="space-y-6">
                    <!-- Address Panel -->
                    <div class="details-panel details-address flex flex-col md:flex-row gap-8 items-stretch mb-6">
                      <div class="flex-1 flex-col items-start">
                        <h3 class="text-xl font-extrabold text-gray-800 mb-1 flex items-center tracking-tight address-header">
                          <v-icon class="mr-2 details-header-icon">mdi-map-marker</v-icon>
                          Address
                        </h3>
                        <p class="text-gray-900 text-lg font-semibold leading-relaxed mt-0">{{ getFullAddress(selectedLocation) }}</p>
                        <button
                          v-if="selectedLocation && (apifyLat && apifyLng || getFullAddress(selectedLocation))"
                          class="details-btn mt-4 px-4 py-2 font-semibold transition-colors"
                          @click="getDirections"
                        >
                          <v-icon left size="20" class="mr-1">mdi-directions</v-icon>
                          Get Directions
                        </button>
                      </div>
                      <div class="w-full md:w-1/2 h-56 md:h-48 rounded-xl overflow-hidden flex-shrink-0 shadow-md border border-blue-100">
                        <iframe
                          v-if="selectedLocation && ((apifyLat && apifyLng) || selectedLocation.place_id || getFullAddress(selectedLocation))"
                          :src="getGoogleMapsSrc()"
                          width="100%"
                          height="100%"
                          style="border:0;"
                          allowfullscreen=""
                          loading="lazy"
                          referrerpolicy="no-referrer-when-downgrade"
                        ></iframe>
                        <div v-else class="flex items-center justify-center h-full bg-gray-100 text-gray-400">
                          No map available
                        </div>
                      </div>
                    </div>

                    <!-- Contact & Web Panel -->
                    <div class="details-panel details-contact flex flex-col md:flex-row gap-8 items-stretch mb-6">
                      <div class="flex-1 flex flex-col justify-center">
                        <h3 class="text-xl font-extrabold text-gray-800 mb-3 flex items-center tracking-tight">
                          <v-icon class="mr-2 details-header-icon">mdi-phone</v-icon>
                          Contact & Web
                        </h3>
                        <div class="space-y-4">
                          <div v-if="selectedLocation.phone">
                            <label class="text-sm font-medium text-gray-500">Phone</label>
                            <p class="text-gray-800">
                              <a :href="`tel:${selectedLocation.phone}`" class="text-blue-600 hover:text-blue-800 hover:underline transition-colors duration-150">
                                {{ selectedLocation.phone }}
                              </a>
                              <button
                                class="details-btn ml-2 px-3 py-1 font-semibold transition-colors text-sm"
                                @click="callBusiness"
                              >
                                <v-icon left size="18" class="mr-1">mdi-phone</v-icon>
                                Call Now
                              </button>
                            </p>
                          </div>
                          <div v-if="selectedLocation.email">
                            <label class="text-sm font-medium text-gray-500">Email</label>
                            <p class="text-gray-800">
                              <a :href="`mailto:${selectedLocation.email}`" class="text-blue-600 hover:text-blue-800 hover:underline transition-colors duration-150">
                                {{ selectedLocation.email }}
                              </a>
                            </p>
                          </div>
                          <div v-if="selectedLocation.website">
                            <label class="text-sm font-medium text-gray-500">Website</label>
                            <p class="text-gray-800">
                              <a :href="selectedLocation.website" target="_blank" class="text-blue-600 hover:text-blue-800 hover:underline transition-colors duration-150">
                                {{ formatWebsite(selectedLocation.website) }}
                              </a>
                              <button
                                class="details-btn ml-2 px-3 py-1 font-semibold transition-colors text-sm"
                                @click="visitWebsite"
                              >
                                <v-icon left size="18" class="mr-1">mdi-web</v-icon>
                                Visit Website
                              </button>
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Business Description -->
                    <div class="details-panel details-description mb-6" v-if="getBusinessDescription(selectedLocation)">
                      <h3 class="text-xl font-extrabold text-gray-800 mb-3 flex items-center tracking-tight">
                        <v-icon class="mr-2 details-header-icon">mdi-text</v-icon>
                        About This Business
                      </h3>
                      <p class="text-gray-700 leading-relaxed text-lg">{{ getBusinessDescription(selectedLocation) }}</p>
                    </div>

                    <!-- Social Links -->
                    <div class="details-panel details-social-links mb-6" v-if="getSocialLinks(selectedLocation).length > 0">
                      <h3 class="text-xl font-extrabold text-gray-800 mb-3 flex items-center tracking-tight">
                        <v-icon class="mr-2 details-header-icon">mdi-share-variant</v-icon>
                        Social Media
                      </h3>
                      <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <a
                          v-for="socialLink in getSocialLinks(selectedLocation)"
                          :key="socialLink.platform"
                          :href="socialLink.url"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="flex items-center gap-3 p-3 rounded-lg bg-white/80 border border-blue-100 hover:bg-blue-50 transition-colors"
                        >
                          <v-icon :color="socialLink.iconColor" size="24">{{ socialLink.icon }}</v-icon>
                          <div class="flex-1">
                            <p class="font-medium text-gray-800">{{ socialLink.platform }}</p>
                            <p class="text-sm text-gray-500 truncate">{{ socialLink.url }}</p>
                          </div>
                          <v-icon small color="gray">mdi-open-in-new</v-icon>
                        </a>
                      </div>
                    </div>

                    <!-- Opening Hours -->
                    <div class="details-panel details-hours mb-6" v-if="getOpeningHours(selectedLocation).length > 0">
                      <h3 class="text-xl font-extrabold text-gray-800 mb-3 flex items-center tracking-tight">
                        <v-icon class="mr-2 details-header-icon">mdi-clock</v-icon>
                        Business Hours
                      </h3>
                      <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div
                          v-for="(dayHours, index) in getOpeningHours(selectedLocation)"
                          :key="index"
                          class="flex justify-between items-center p-3 rounded-lg bg-white/80 border border-blue-100 mb-2"
                          :class="isCurrentDay(dayHours.day) ? 'current-day' : ''"
                        >
                          <span class="font-medium text-gray-700">{{ dayHours.day }}</span>
                          <span class="text-gray-600">{{ dayHours.hours || 'Closed' }}</span>
                        </div>
                      </div>
                    </div>

                    <!-- Categories & Services -->
                    <!-- Remove the following block from the details panel:
                    <div class="details-panel details-categories mb-6">
                      <h3 class="text-xl font-extrabold text-gray-800 mb-3 flex items-center tracking-tight">
                        <v-icon class="mr-2 details-header-icon">mdi-tag-multiple</v-icon>
                        Categories & Services
                      </h3>
                      <div class="space-y-4">
                        <div v-if="getPrimaryCategory(selectedLocation)">
                          <label class="text-sm font-medium text-gray-500">Primary Category</label>
                          <p class="text-gray-800 font-semibold text-lg">{{ getPrimaryCategory(selectedLocation) }}</p>
                        </div>
                        <div v-if="getAdditionalCategories(selectedLocation).length > 0">
                          <label class="text-sm font-medium text-gray-500">Additional Categories</label>
                          <div class="flex flex-wrap gap-2 mt-2">
                            <span
                              v-for="category in getAdditionalCategories(selectedLocation)"
                              :key="category"
                              class="details-btn px-2 py-1 rounded-full text-sm font-semibold mr-2 mb-2 inline-block"
                            >
                              {{ category }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    -->

                    <!-- Reviews Panel -->
                    <div class="details-panel details-reviews mb-6">
                      <h3 class="text-xl font-extrabold text-gray-800 mb-3 flex items-center tracking-tight">
                        <v-icon class="mr-2 details-header-icon">mdi-star</v-icon>
                        Google Reviews
                      </h3>
                      <div v-if="isReviewsLoading" class="text-center py-4">
                        <v-progress-circular indeterminate color="primary" size="32"></v-progress-circular>
                        <p class="text-gray-500 mt-2">Loading reviews...</p>
                      </div>
                      <div v-else-if="reviews.length === 0" class="text-gray-500 py-4">No reviews found for this location.</div>
                      <div v-else class="space-y-4">
                        <div v-for="review in paginatedReviews" :key="review.reviewId" class="review-item">
                          <div class="flex items-center gap-3 mb-2">
                            <img
                              :src="review.profilePhotoUrl || 'https://www.gravatar.com/avatar/?d=mp'"
                              alt="profile"
                              class="w-8 h-8 rounded-full object-cover"
                              @error="event => event.target.src = 'https://www.gravatar.com/avatar/?d=mp'"
                            />
                            <div>
                              <div class="review-author">{{ review.displayName }}</div>
                              <div class="flex items-center gap-2">
                                <div class="flex">
                                  <span v-for="index in 5" :key="index">
                                    <svg :class="index <= review.starRating ? 'text-yellow-400' : 'text-gray-300'" class="w-3 h-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                    </svg>
                                  </span>
                                </div>
                                <div class="review-date">{{ formatDate(review.updateTime) }}</div>
                              </div>
                            </div>
                          </div>
                          <div class="review-comment">{{ review.comment }}</div>
                        </div>
                      </div>
                      
                      <!-- Pagination Controls -->
                      <div v-if="reviews.length > reviewsPerPage" class="pagination-container">
                        <div class="pagination-info">
                          Showing {{ (reviewsPage - 1) * reviewsPerPage + 1 }} to {{ Math.min(reviewsPage * reviewsPerPage, reviews.length) }} of {{ reviews.length }} reviews
                        </div>
                        <div class="flex items-center gap-2">
                          <button
                            @click="prevReviewPage"
                            :disabled="!hasPrevReviewPage"
                            class="details-btn px-3 py-1 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <v-icon left size="16">mdi-chevron-left</v-icon>
                            Previous
                          </button>
                          <span class="pagination-page-info">
                            Page {{ reviewsPage }} of {{ totalReviewPages }}
                          </span>
                          <button
                            @click="nextReviewPage"
                            :disabled="!hasNextReviewPage"
                            class="details-btn px-3 py-1 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Next
                            <v-icon right size="16">mdi-chevron-right</v-icon>
                          </button>
                        </div>
                      </div>
                    </div>

                    <!-- Posts Panel -->
                    <div class="details-panel details-posts mb-6">
                      <h3 class="text-xl font-extrabold text-gray-800 mb-3 flex items-center tracking-tight">
                        <v-icon class="mr-2 details-header-icon">mdi-post</v-icon>
                        Recent Posts
                      </h3>
                      <div v-if="isPostsLoading" class="text-center py-4">
                        <v-progress-circular indeterminate color="primary" size="32"></v-progress-circular>
                        <p class="text-gray-500 mt-2">Loading posts...</p>
                      </div>
                      <div v-else-if="posts.length === 0" class="text-gray-500 py-4">No posts found for this location.</div>
                      <div v-else class="space-y-6">
                        <div v-for="post in posts" :key="post.id" class="bg-gray-50 rounded-lg p-4 shadow-sm border border-gray-100">
                          <!-- Social Media Icons and Date -->
                          <div class="flex items-center gap-2 mb-3">
                            <div class="flex gap-1">
                              <v-avatar
                                tile
                                size="20" 
                                v-for="(socialMedia, index) in post.social_media"
                                :key="index"
                              >
                                <v-img
                                  :src="socialMedia.logo_url"
                                  :alt="socialMedia.name"
                                ></v-img>
                              </v-avatar>
                            </div>
                            <span class="text-xs text-gray-500">
                              {{ formatDate(post.created_at?.seconds ? post.created_at.seconds * 1000 : post.created_at) }}
                            </span>
                          </div>
                          
                          <!-- Images on Top - Larger Size -->
                          <div v-if="post.images && post.images.length > 0" class="mb-4">
                            <div class="grid grid-cols-1 gap-3">
                              <img
                                v-for="(image, index) in post.images.slice(0, 2)"
                                :key="index"
                                :src="image"
                                alt="post image"
                                class="w-full h-auto max-h-96 object-contain bg-gray-100 rounded-lg shadow-md"
                              />
                              <div v-if="post.images.length > 2" class="grid grid-cols-3 gap-2">
                                <img
                                  v-for="(image, index) in post.images.slice(2, 5)"
                                  :key="index"
                                  :src="image"
                                  alt="post image"
                                  class="w-full h-auto max-h-48 object-contain bg-gray-100 rounded-lg shadow-sm"
                                />
                                <div v-if="post.images.length > 5" class="h-24 bg-gray-200 rounded-lg flex items-center justify-center text-xs text-gray-500">
                                  +{{ post.images.length - 5 }}
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          <!-- Videos on Top - Larger Size -->
                          <div v-if="post.videos && post.videos.length > 0" class="mb-4">
                            <div class="grid grid-cols-1 gap-3">
                              <div
                                v-for="(video, index) in post.videos.slice(0, 2)"
                                :key="index"
                                class="w-full h-48 bg-gray-200 rounded-lg flex items-center justify-center shadow-md relative"
                              >
                                <v-icon size="48" color="white" class="absolute">mdi-play-circle</v-icon>
                                <div class="absolute bottom-2 right-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
                                  Video
                                </div>
                              </div>
                              <div v-if="post.videos.length > 2" class="grid grid-cols-3 gap-2">
                                <div
                                  v-for="(video, index) in post.videos.slice(2, 5)"
                                  :key="index"
                                  class="h-24 bg-gray-200 rounded-lg flex items-center justify-center relative"
                                >
                                  <v-icon size="24" color="white">mdi-play</v-icon>
                                </div>
                                <div v-if="post.videos.length > 5" class="h-24 bg-gray-200 rounded-lg flex items-center justify-center text-xs text-gray-500">
                                  +{{ post.videos.length - 5 }}
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          <!-- Post Title -->
                          <div v-if="post.name" class="text-lg font-semibold text-gray-800 mb-2">
                            {{ post.name }}
                          </div>
                          
                          <!-- Post Text at Bottom -->
                          <div class="text-gray-700 text-base leading-relaxed">
                            <!-- Debug info (remove in production) -->
                            <div class="text-xs text-gray-400 mb-1">
                              Post ID: {{ post.id }} | Expanded: {{ expandedPosts[post.id] }}
                            </div>
                            <span v-if="post.text">
                              {{ (post.text.length > 200 && !expandedPosts[post.id]) ? post.text.substring(0, 200) + '...' : post.text }}
                            </span>
                            <span 
                              v-if="post.text && post.text.length > 200" 
                              @click="toggleReadMore(post.id)" 
                              class="text-blue-500 hover:underline ml-1 font-semibold text-sm cursor-pointer"
                            >
                              {{ expandedPosts[post.id] ? 'Show less' : 'Show more' }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Action Buttons -->

                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
    <!-- Footer Section -->
    <footer class="store-footer"
      :style="{ '--footer-primary-color': storeLocatorStyles.primaryColor || '#000' }"
    >
      <div class="footer-links">
        <template v-for="(city, idx) in footerCities">
          <a
            :key="city.city"
            href="#"
            class="footer-link"
            @click.prevent="selectedCityFilter = city.city === 'Unknown' ? '__other__' : city.city"
          >
            {{ brandName }} in {{ city.city === 'Unknown' ? 'Other Locations' : city.city }}
          </a>
          <span v-if="idx !== footerCities.length - 1" class="footer-separator"> | </span>
        </template>
      </div>
    </footer>
  </div>
</template>

<script>
import { auth, db } from "@/utils/firebase.utils";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc,
  orderBy,
  limit,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { functions } from "@/utils/firebase.utils";
import PostCard from '@/components/post/PostCard.vue';
import { 
  updateMetaTags, 
  generateStoreLocatorTitle, 
  generateStoreLocatorDescription, 
  generateStoreLocatorStructuredData, 
  injectStructuredData, 
  cleanupMetaTags 
} from "@/utils/seo.utils";
export default {
  components: {
    PostCard
  },
  props: {
    embedUid: {
      type: String,
      default: null
    },
    embedWid: {
      type: String,
      default: null
    },
    embedSlug: {
      type: String,
      default: null
    },
    storeLocatorStyles: {
      type: Object,
      default: () => ({})
    },
    disableNavigation: {
      type: Boolean,
      default: false
    },
    disableFavicon: {
      type: Boolean,
      default: false
    },
    disablePageTitle: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      isLoading: false,
      locations: [],
      locationTree: {},
      selectedLocation: null,
      searchQuery: "",
      expandedCountries: [],
      expandedStates: [],
      platformLogoCache: {},
      workspaceData: null,
      apifyLat: null,
      apifyLng: null,
      _originalFaviconHref: null,
      _originalPageTitle: null,
      logoLoadError: false,
      logoLoaded: false,
      selectedTab: null,
      selectedCityFilter: "",
      // Add review state
      reviews: [],
      isReviewsLoading: false,
      // Add pagination state for reviews
      reviewsPage: 1,
      reviewsPerPage: 5,
      totalReviews: 0,
      // Add posts state
      posts: [],
      isPostsLoading: false,
      expandedPosts: {},
      coverPhotoError: false,
      coverPhotoLoading: false,
    };
  },
  computed: {
    currentUser() {
      return this.$store.getters.currentUser;
    },
    authUser() {
      return this.$store.getters.authUser;
    },
    WID() {
      const wid = this.embedWid || this.$store.getters.WID;
      console.log("Current WID:", wid);
      return wid;
    },
    currentUID() {
      // Use embed UID from URL path
      if (this.embedUid) {
        console.log("Using embed UID from URL:", this.embedUid);
        return this.embedUid;
      }
      
      // Fall back to store UID
      const storeUid = this.$store.getters.currentUID;
      console.log("Using store UID:", storeUid);
      return storeUid;
    },
    globalStores() {
      return this.$store.getters.globalStores || [];
    },
    mergedStoreLocatorStyles() {
      // Merge prop with workspace data, prop takes precedence
      const workspaceStyles = this.workspaceData?.store_locator_styles || {};
      return { ...workspaceStyles, ...this.storeLocatorStyles };
    },
    totalLocations() {
      return this.locations.length;
    },
    uniqueCities() {
      // Get unique city names from all locations
      const cities = new Set();
      this.locations.forEach(loc => {
        if (loc.city) cities.add(loc.city);
      });
      return Array.from(cities).sort();
    },
    filteredLocationTree() {
      // If city filter is applied, filter locations by city
      let filteredTree = this.locationTree;
      if (this.selectedCityFilter) {
        const filtered = {};
        Object.entries(this.locationTree).forEach(([country, countryData]) => {
          const filteredStates = {};
          let countryHasResults = false;
          Object.entries(countryData.states).forEach(([state, stateData]) => {
            let matchingLocations;
            if (this.selectedCityFilter === "__other__") {
              // Show locations with no city
              matchingLocations = stateData.locations.filter(
                location => !location.city
              );
            } else {
              // Show locations matching the selected city
              matchingLocations = stateData.locations.filter(
                location => location.city === this.selectedCityFilter
              );
            }
            if (matchingLocations.length > 0) {
              filteredStates[state] = {
                ...stateData,
                locations: matchingLocations
              };
              countryHasResults = true;
            }
          });
          if (countryHasResults) {
            filtered[country] = {
              ...countryData,
              states: filteredStates,
              totalCount: Object.values(filteredStates).reduce((sum, state) => sum + state.locations.length, 0)
            };
          }
        });
        filteredTree = filtered;
      }
      // Apply search filter if present
      if (!this.searchQuery.trim()) {
        return filteredTree;
      }
      const query = this.searchQuery.toLowerCase();
      const filtered = {};
      Object.entries(filteredTree).forEach(([country, countryData]) => {
        const filteredStates = {};
        let countryHasResults = false;
        Object.entries(countryData.states).forEach(([state, stateData]) => {
          const matchingLocations = stateData.locations.filter(location => {
            const searchableText = [
              location.name,
              location.storeLocation,
              location.city,
              location.state,
              location.country,
              location.zipcode,
              location.sub_locality
            ].filter(Boolean).join(' ').toLowerCase();
            return searchableText.includes(query);
          });
          if (matchingLocations.length > 0) {
            filteredStates[state] = {
              ...stateData,
              locations: matchingLocations
            };
            countryHasResults = true;
          }
        });
        if (countryHasResults) {
          filtered[country] = {
            ...countryData,
            states: filteredStates,
            totalCount: Object.values(filteredStates).reduce((sum, state) => sum + state.locations.length, 0)
          };
        }
      });
      return filtered;
    },
    isEmbed() {
      return !!this.embedUid;
    },
    storeLogo() {
      if (!this.workspaceData) {
        console.log("🚫 [BRAND LOGO] No workspace data available for logo");
        return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNMTIgMkM2LjQ4IDIgMiA2LjQ4IDIgMTJzNC40OCAxMCAxMCAxMCAxMC00LjQ4IDEwLTEwUzE3LjUyIDIgMTIgMnptMCAxOGMtNC40MSAwLTgtMy41OS04LThzMy41OS04IDgtOCA4IDMuNTkgOCA4LTMuNTkgOC04IDh6IiBmaWxsPSIjZTVlN2ViIi8+PC9zdmc+';
      }
      
      // Check multiple possible logo fields in order of preference
      const logo = this.workspaceData.logo_original || 
                   this.workspaceData.logo_url || 
                   this.workspaceData.logo_dark || 
                   this.workspaceData.logo_transparent ||
                   this.workspaceData.logo;
      
      console.log("🏢 [BRAND LOGO] Workspace data:", this.workspaceData);
      console.log("🔍 [BRAND LOGO] Available logo fields:", {
        logo_original: this.workspaceData.logo_original,
        logo_url: this.workspaceData.logo_url,
        logo_dark: this.workspaceData.logo_dark,
        logo_transparent: this.workspaceData.logo_transparent,
        logo: this.workspaceData.logo
      });
      console.log("✅ [BRAND LOGO] Selected logo URL:", logo);
      console.log("📊 [BRAND LOGO] Logo URL type:", typeof logo);
      console.log("🔗 [BRAND LOGO] Logo URL length:", logo ? logo.length : 0);
      
      return logo || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNMTIgMkM2LjQ4IDIgMiA2LjQ4IDIgMTJzNC40OCAxMCAxMCAxMCAxMC00LjQ4IDEwLTEwUzE3LjUyIDIgMTIgMnptMCAxOGMtNC40MSAwLTgtMy41OS04LThzMy41OS04IDgtOCA4IDMuNTkgOCA4LTMuNTkgOC04IDh6IiBmaWxsPSIjZTVlN2ViIi8+PC9zdmc+';
    },
    brandName() {
      if (!this.workspaceData) {
        console.log("No workspace data available for brand name");
        return 'Loading...';
      }
      const name = this.workspaceData.name;
      console.log("Brand name:", name);
      return name || 'Store Brand';
    },
    fullStars() {
      if (!this.selectedLocation || !this.selectedLocation.averageRating) return 0;
      return Math.floor(this.selectedLocation.averageRating);
    },
    decimalPart() {
      if (!this.selectedLocation || !this.selectedLocation.averageRating) return 0;
      return this.selectedLocation.averageRating - this.fullStars;
    },
    styleVars() {
      const headerStyles = this.mergedStoreLocatorStyles.header_styles || {};
      const sidepanelStyles = this.mergedStoreLocatorStyles.sidepanel_styles || {};
      const detailsStyles = this.mergedStoreLocatorStyles.details_styles || {};
      const common = detailsStyles.common || {};
      const button = (detailsStyles.button || {});
      function getBtnVar(key, fallback) {
        return button[key] || fallback;
      }
      function getCommonVar(key, fallback) {
        return common[key] || fallback;
      }
      return {
        '--store-primary': this.mergedStoreLocatorStyles.primaryColor || '#1773EA',
        '--store-secondary': this.mergedStoreLocatorStyles.secondaryColor || '#6B7280',
        '--store-panel-bg': this.mergedStoreLocatorStyles.panelBg || 'linear-gradient(90deg, #eff6ff 0%, #e0e7ff 100%)',
        '--store-header-text': this.mergedStoreLocatorStyles.headerTextColor || '#222',
        '--store-body-text': this.mergedStoreLocatorStyles.bodyTextColor || '#333',
        '--store-border-radius': this.mergedStoreLocatorStyles.borderRadius || '1.25rem',
        '--store-font-family': this.mergedStoreLocatorStyles.fontFamily || 'Inter, sans-serif',
        '--store-button-bg': this.mergedStoreLocatorStyles.buttonBg || '#1773EA',
        '--store-button-text': this.mergedStoreLocatorStyles.buttonTextColor || '#fff',
        '--store-selected-bg': this.mergedStoreLocatorStyles.selectedBg || 'rgba(23, 115, 234, 0.1)',
        '--store-selected-border': this.mergedStoreLocatorStyles.selectedBorder || '#1773EA',
        '--store-selected-text': this.mergedStoreLocatorStyles.selectedText || '#1773EA',
        '--store-logo-size-mobile': headerStyles.logoSizeMobile || '2.25rem',
        '--store-logo-size-desktop': headerStyles.logoSizeDesktop || '3rem',
        '--store-header-bg': headerStyles.headerBg || '#fff',
        '--store-tab-text': headerStyles.tabTextColor || '#333',
        '--store-tab-hover-text': headerStyles.tabHoverTextColor || '#1773EA',
        '--store-tab-selected-text': headerStyles.tabSelectedTextColor || '#fff',
        '--store-tab-bg': headerStyles.tabBg || '#f3f4f6',
        '--store-tab-hover-bg': headerStyles.tabHoverBg || '#e0e7ff',
        '--store-tab-selected-bg': headerStyles.tabSelectedBg || '#1773EA',
        '--store-search-border': headerStyles.searchBorderColor || '#bdb4e7',
        '--store-search-radius': headerStyles.searchBorderRadius || '0.75rem',
        '--store-search-border-size': headerStyles.searchBorderSize || '2px',
        '--store-brand-name-size': headerStyles.brandNameSize || '1.375rem',
        '--store-brand-name-color': headerStyles.brandNameColor || '#000000',
        '--sidepanel-selected-bg': sidepanelStyles.selectedBg || 'rgba(23, 115, 234, 0.15)',
        '--sidepanel-selected-text': sidepanelStyles.selectedText || 'rgba(23, 115, 234, 1)',
        '--sidepanel-selected-border': sidepanelStyles.selectedBorder || 'rgba(23, 115, 234, 1)',
        '--sidepanel-selected-radius': sidepanelStyles.selectedRadius || '1.25rem',
        '--sidepanel-hover-bg': sidepanelStyles.hoverBg || 'rgba(23, 115, 234, 0.08)',
        '--sidepanel-hover-text': sidepanelStyles.hoverText || 'rgba(23, 115, 234, 1)',
        '--sidepanel-hover-border': sidepanelStyles.hoverBorder || 'rgba(23, 115, 234, 1)',
        '--sidepanel-hover-border-size': sidepanelStyles.hoverBorderSize || '2px',
        '--sidepanel-hover-radius': sidepanelStyles.hoverRadius || '1.25rem',
        '--sidepanel-unselected-bg': sidepanelStyles.unselectedBg || '#fff',
        '--sidepanel-unselected-text': sidepanelStyles.unselectedText || '#222',
        '--sidepanel-unselected-border': sidepanelStyles.unselectedBorder || 'transparent',
        '--sidepanel-unselected-border-size': sidepanelStyles.unselectedBorderSize || '1px',
        '--sidepanel-unselected-radius': sidepanelStyles.unselectedRadius || '0.75rem',
        '--sidepanel-icon': sidepanelStyles.iconColor || 'rgba(23, 115, 234, 1)',
        '--sidepanel-chevron': sidepanelStyles.chevronColor || 'rgba(23, 115, 234, 0.7)',
        '--sidepanel-selected-border-size': sidepanelStyles.selectedBorderSize || '2px',
        '--details-address-bg': getCommonVar('bg', '#f0f0f0'),
        '--details-address-text': getCommonVar('textColor', '#222'),
        '--details-address-border': getCommonVar('borderColor', '#1773EA'),
        '--details-address-border-size': getCommonVar('borderSize', '2px'),
        '--details-address-radius': getCommonVar('borderRadius', '1.25rem'),
        '--details-contact-bg': getCommonVar('bg', '#f0f0f0'),
        '--details-contact-text': getCommonVar('textColor', '#222'),
        '--details-contact-border': getCommonVar('borderColor', '#1773EA'),
        '--details-contact-border-size': getCommonVar('borderSize', '2px'),
        '--details-contact-radius': getCommonVar('borderRadius', '1.25rem'),
        '--details-description-bg': getCommonVar('bg', '#f0f0f0'),
        '--details-description-text': getCommonVar('textColor', '#222'),
        '--details-description-border': getCommonVar('borderColor', '#1773EA'),
        '--details-description-border-size': getCommonVar('borderSize', '2px'),
        '--details-description-radius': getCommonVar('borderRadius', '1.25rem'),
        '--details-cover-photo-bg': getCommonVar('bg', '#f0f0f0'),
        '--details-cover-photo-text': getCommonVar('textColor', '#222'),
        '--details-cover-photo-border': getCommonVar('borderColor', '#1773EA'),
        '--details-cover-photo-border-size': getCommonVar('borderSize', '2px'),
        '--details-cover-photo-radius': getCommonVar('borderRadius', '1.25rem'),
        '--details-social-links-bg': getCommonVar('bg', '#f0f0f0'),
        '--details-social-links-text': getCommonVar('textColor', '#222'),
        '--details-social-links-border': getCommonVar('borderColor', '#1773EA'),
        '--details-social-links-border-size': getCommonVar('borderSize', '2px'),
        '--details-social-links-radius': getCommonVar('borderRadius', '1.25rem'),
        '--details-hours-bg': getCommonVar('bg', '#f0f0f0'),
        '--details-hours-text': getCommonVar('textColor', '#222'),
        '--details-hours-border': getCommonVar('borderColor', '#1773EA'),
        '--details-hours-border-size': getCommonVar('borderSize', '2px'),
        '--details-hours-radius': getCommonVar('borderRadius', '1.25rem'),
        '--details-categories-bg': getCommonVar('bg', '#f0f0f0'),
        '--details-categories-text': getCommonVar('textColor', '#222'),
        '--details-categories-border': getCommonVar('borderColor', '#1773EA'),
        '--details-categories-border-size': getCommonVar('borderSize', '2px'),
        '--details-categories-radius': getCommonVar('borderRadius', '1.25rem'),
        '--details-posts-bg': getCommonVar('bg', '#f0f0f0'),
        '--details-posts-text': getCommonVar('textColor', '#222'),
        '--details-posts-border': getCommonVar('borderColor', '#1773EA'),
        '--details-posts-border-size': getCommonVar('borderSize', '2px'),
        '--details-posts-radius': getCommonVar('borderRadius', '1.25rem'),
        '--details-actions-bg': getCommonVar('bg', '#f0f0f0'),
        '--details-actions-text': getCommonVar('textColor', '#222'),
        '--details-actions-border': getCommonVar('borderColor', '#1773EA'),
        '--details-actions-border-size': getCommonVar('borderSize', '2px'),
        '--details-actions-radius': getCommonVar('borderRadius', '1.25rem'),
        '--details-btn-text': getBtnVar('textColor', '#fff'),
        '--details-btn-bg': getBtnVar('bg', '#1773EA'),
        '--details-btn-border': getBtnVar('borderColor', '#1773EA'),
        '--details-btn-radius': getBtnVar('borderRadius', '0.75rem'),
        '--details-btn-border-size': getBtnVar('borderSize', '1.5px'),
        '--details-btn-hover-text': getBtnVar('hoverTextColor', '#fff'),
        '--details-btn-hover-bg': getBtnVar('hoverBg', '#125bb5'),
        '--details-btn-hover-border': getBtnVar('hoverBorderColor', '#125bb5'),
        '--details-btn-hover-border-size': getBtnVar('hoverBorderSize', '1.5px'),
        '--details-btn-hover-border-radius': getBtnVar('hoverBorderRadius', '0.75rem'),
        '--details-btn-selected-text': getBtnVar('selectedTextColor', '#fff'),
        '--details-btn-selected-bg': getBtnVar('selectedBg', '#125bb5'),
        '--details-btn-selected-border': getBtnVar('selectedBorderColor', '#125bb5'),
        '--details-btn-selected-border-size': getBtnVar('selectedBorderSize', '1.5px'),
        '--details-btn-selected-border-radius': getBtnVar('selectedBorderRadius', '0.75rem'),
        '--details-header-bg': getCommonVar('bg', '#f0f0f0'),
        '--details-header-text': getCommonVar('textColor', '#222'),
        '--details-header-border': getCommonVar('borderColor', '#1773EA'),
        '--details-header-border-size': getCommonVar('borderSize', '2px'),
        '--details-header-radius': getCommonVar('borderRadius', '1.25rem'),
        // Basic color scheme for details panel
        '--details-primary': getCommonVar('primary', '#1976d2'),
        '--details-secondary': getCommonVar('secondary', '#424242'),
        '--details-accent': getCommonVar('accent', '#82B1FF'),
        '--details-info': getCommonVar('info', '#2196F3'),
        '--details-success': getCommonVar('success', '#4CAF50'),
        '--details-warning': getCommonVar('warning', '#FFC107'),
        '--details-error': getCommonVar('error', '#FF5252'),
        '--details-posts-border-size': getCommonVar('borderSize', '2px'),
        '--details-posts-radius': getCommonVar('borderRadius', '1.25rem'),
        '--details-actions-bg': getCommonVar('bg', '#f0f0f0'),
        '--details-actions-text': getCommonVar('textColor', '#222'),
        '--details-actions-border': getCommonVar('borderColor', '#1773EA'),
        '--details-actions-border-size': getCommonVar('borderSize', '2px'),
        '--details-actions-radius': getCommonVar('borderRadius', '1.25rem'),
        '--details-reviews-bg': getCommonVar('bg', '#f0f0f0'),
        '--details-reviews-text': getCommonVar('textColor', '#222'),
        '--details-reviews-border': getCommonVar('borderColor', '#1773EA'),
        '--details-reviews-border-size': getCommonVar('borderSize', '2px'),
        '--details-reviews-radius': getCommonVar('borderRadius', '1.25rem'),
        '--details-btn-text': getBtnVar('textColor', '#fff'),
      };
    },
    logoIconSize() {
      // Convert the mobile logo size to a number for the icon size
      const mobileSize = this.mergedStoreLocatorStyles.logoSizeMobile || '2.25rem';
      const desktopSize = this.mergedStoreLocatorStyles.logoSizeDesktop || '3rem';
      
      // Extract the numeric value and convert to a reasonable icon size
      const mobileNum = parseInt(mobileSize);
      const desktopNum = parseInt(desktopSize);
      
      // Use the larger size for the icon, but cap it at a reasonable maximum
      const iconSize = Math.min(Math.max(mobileNum, desktopNum), 64);
      
      return iconSize.toString();
    },
    headerTabs() {
      return this.mergedStoreLocatorStyles.headerTabs || [];
    },
    footerCities() {
      // Collect unique cities with at least one store from all locations (not filtered)
      const seen = new Set();
      const cities = [];
      Object.values(this.locationTree).forEach(countryData => {
        Object.values(countryData.states).forEach(stateData => {
          stateData.locations.forEach(location => {
            const city = location.city || location.state || 'Unknown';
            if (!seen.has(city)) {
              seen.add(city);
              cities.push({
                city,
                location
              });
            }
          });
        });
      });
      return cities;
    },
    showSidepanelIcons() {
      // Use string: 'all' (default) or 'none'
      const val = this.mergedStoreLocatorStyles.sidepanel_styles && this.mergedStoreLocatorStyles.sidepanel_styles.showIcons;
      return (typeof val === 'string' ? val : 'all');
    },
    // Add computed properties for review pagination
    paginatedReviews() {
      const startIndex = (this.reviewsPage - 1) * this.reviewsPerPage;
      const endIndex = startIndex + this.reviewsPerPage;
      return this.reviews.slice(startIndex, endIndex);
    },
    totalReviewPages() {
      return Math.ceil(this.reviews.length / this.reviewsPerPage);
    },
    hasNextReviewPage() {
      return this.reviewsPage < this.totalReviewPages;
    },
    hasPrevReviewPage() {
      return this.reviewsPage > 1;
    },
  },
  watch: {
    selectedLocation: {
      immediate: true,
      handler(newVal) {
        console.log('[selectedLocation watcher] Location changed:', newVal);
        console.log('[selectedLocation watcher] New location ID:', newVal?.id);
        console.log('[selectedLocation watcher] New location name:', newVal?.name || newVal?.storeLocation);
        
        // Reset cover photo error when location changes
        this.coverPhotoError = false;
        this.coverPhotoLoading = false;
        
        if (newVal && newVal.id) {
          console.log('[selectedLocation watcher] Fetching data for location:', newVal.id);
          this.fetchApifyLatLng(newVal.id);
          this.fetchReviewsForLocation(newVal);
          this.fetchPostsForLocation(newVal);
        } else {
          console.log('[selectedLocation watcher] Clearing location data');
          this.apifyLat = null;
          this.apifyLng = null;
          this.reviews = [];
          this.posts = [];
          // Reset pagination when clearing location
          this.reviewsPage = 1;
          // Reset expanded posts state when clearing location
          this.expandedPosts = {};
        }
      }
    },
    workspaceData: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          console.log("Workspace data updated, updating favicon...");
          this.updateFavicon();
        }
      }
    },
    storeLogo: {
      immediate: true,
      handler(newVal) {
        if (newVal && this.workspaceData) {
          console.log("Store logo updated, updating favicon...", newVal);
          this.updateFavicon();
        }
      }
    },
    brandName: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal !== 'Loading...') {
          console.log("Brand name updated, updating page title...", newVal);
          this.updatePageTitle();
        }
      }
    },
    headerTabs: {
      immediate: true,
      handler(newTabs) {
        if (newTabs && newTabs.length && !this.selectedTab) {
          this.selectedTab = newTabs[0].value;
        }
      }
    },
    // Watch for slug changes in the route to support browser navigation
    '$route.params.slug': {
      immediate: false,
      handler(newSlug) {
        if (!newSlug || !this.locations || this.locations.length === 0) return;
        const slugify = str => str && str.toString().trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
        const match = this.locations.find(loc => slugify(loc.name || loc.storeLocation) === newSlug);
        if (match && (!this.selectedLocation || this.selectedLocation.id !== match.id)) {
          this.selectedLocation = match;
          // Expand treeview for selected location
          const country = match.country || 'Other Countries';
          const state = match.state || 'Other States';
          if (!this.expandedCountries.includes(country)) {
            this.expandedCountries.push(country);
          }
          const stateKey = `${country}-${state}`;
          if (!this.expandedStates.includes(stateKey)) {
            this.expandedStates.push(stateKey);
          }
        }
      }
    },
    selectedCityFilter: {
      immediate: false,
      handler(newCity) {
        if (!newCity) return; // If filter is cleared, do nothing
        // Get all locations in the filtered city
        let filteredLocations = [];
        if (newCity === "__other__") {
          filteredLocations = this.locations.filter(loc => !loc.city);
        } else {
          filteredLocations = this.locations.filter(loc => loc.city === newCity);
        }
        // If the current selectedLocation is in the filtered city, do nothing
        if (
          this.selectedLocation &&
          ((newCity === "__other__" && !this.selectedLocation.city) ||
           (this.selectedLocation.city === newCity))
        ) {
          return;
        }
        // Otherwise, select the first location in the filtered city (if any)
        if (filteredLocations.length > 0) {
          const firstLoc = filteredLocations[0];
          this.selectedLocation = firstLoc;
          // Expand treeview for selected location
          const country = firstLoc.country || 'Other Countries';
          const state = firstLoc.state || 'Other States';
          if (!this.expandedCountries.includes(country)) {
            this.expandedCountries.push(country);
          }
          const stateKey = `${country}-${state}`;
          if (!this.expandedStates.includes(stateKey)) {
            this.expandedStates.push(stateKey);
          }
        }
      }
    },
    // Watch for embedSlug prop changes
    embedSlug: {
      immediate: false,
      handler(newSlug) {
        if (newSlug && this.locations && this.locations.length > 0) {
          this.selectLocationBySlug(newSlug);
        }
      }
    },
    // Watch for reviews changes to reset pagination
    reviews: {
      handler(newReviews) {
        // Reset to first page when reviews change
        this.reviewsPage = 1;
      }
    },
    
    // Watch for workspace data changes to update SEO
    workspaceData: {
      handler(newWorkspaceData) {
        if (newWorkspaceData && this.locations.length > 0) {
          console.log('[StoreLocator] Workspace data changed, updating SEO');
          this.updateSEO();
        }
      },
      deep: true
    },
    
    // Watch for locations changes to update SEO
    locations: {
      handler(newLocations) {
        if (newLocations.length > 0 && this.workspaceData) {
          console.log('[StoreLocator] Locations changed, updating SEO');
          this.updateSEO();
        }
      },
      deep: true
    },
  },
  methods: {
    selectLocationBySlug(slug) {
      console.log('[selectLocationBySlug] Selecting location by slug:', slug);
      const slugify = str => str && str.toString().trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
      const match = this.locations.find(loc => slugify(loc.name || loc.storeLocation) === slug);
      if (match) {
        console.log('[selectLocationBySlug] Found matching location:', match);
        this.selectedLocation = match;
        // Expand treeview for selected location
        const country = match.country || 'Other Countries';
        const state = match.state || 'Other States';
        if (!this.expandedCountries.includes(country)) {
          this.expandedCountries.push(country);
        }
        const stateKey = `${country}-${state}`;
        if (!this.expandedStates.includes(stateKey)) {
          this.expandedStates.push(stateKey);
        }
      } else {
        console.log('[selectLocationBySlug] No matching location found for slug:', slug);
      }
    },

    async fetchWorkspaceData() {
      if (!this.WID) {
        console.error("Cannot fetch workspace data: WID is empty");
        return;
      }
      if (!this.currentUID) {
        console.error("Cannot fetch workspace data: currentUID is empty");
        return;
      }
      
      console.log("Fetching workspace data for WID:", this.WID, "UID:", this.currentUID);
      
      try {
        // First try to get from global stores
        const workspaceFromStores = this.globalStores.find(store => store.id === this.WID);
        if (workspaceFromStores) {
          console.log("Found workspace in global stores:", workspaceFromStores);
          this.workspaceData = workspaceFromStores;
          return;
        }

        // If not in global stores, try Firestore
        const workspaceRef = doc(db, "users", this.currentUID, "fly_workspaces", this.WID);
        console.log("Fetching from Firestore path:", workspaceRef.path);
        
        const workspaceDoc = await getDoc(workspaceRef);
        
        if (workspaceDoc.exists()) {
          const data = workspaceDoc.data();
          console.log("Workspace data fetched from Firestore:", data);
          this.workspaceData = data;
        } else {
          console.error("Workspace not found in Firestore for WID:", this.WID);
          // Try to get from any available stores as last resort
          const allStores = [...this.globalStores, ...this.locations];
          const workspaceFromAnyStore = allStores.find(store => store.id === this.WID);
          if (workspaceFromAnyStore) {
            console.log("Found workspace in available stores:", workspaceFromAnyStore);
            this.workspaceData = workspaceFromAnyStore;
          }
        }
      } catch (error) {
        console.error("Error fetching workspace data:", error);
        // Try to get from any available stores as last resort
        const allStores = [...this.globalStores, ...this.locations];
        const workspaceFromAnyStore = allStores.find(store => store.id === this.WID);
        if (workspaceFromAnyStore) {
          console.log("Found workspace in available stores after error:", workspaceFromAnyStore);
          this.workspaceData = workspaceFromAnyStore;
          this.storeLocatorStyles = workspaceFromAnyStore.store_locator_styles || {};
        }
      }
    },

    async fetchLocations() {
      this.isLoading = true;
      try {
        // Use global stores from Vuex if available
        if (this.globalStores && this.globalStores.length > 0) {
          console.log("Using globalStores from Vuex:", this.globalStores.length);
          // Try to get workspace data from global stores first
          const workspaceFromStores = this.globalStores.find(store => store.id === this.WID);
          if (workspaceFromStores) {
            console.log("Using workspace data from global stores:", workspaceFromStores);
            this.workspaceData = workspaceFromStores;
            this.storeLocatorStyles = workspaceFromStores.store_locator_styles || {};
          } else {
            // If not found in global stores, fetch from Firestore
            await this.fetchWorkspaceData();
          }
          this.processStores(this.globalStores);
        } else {
          // Fallback: fetch directly from Firestore
          console.log("Fetching locations directly from Firestore");
          if (!this.currentUID || !this.WID) {
            console.error("Cannot fetch locations: missing UID or WID");
            return;
          }

          const storesRef = collection(
            db,
            "users",
            this.currentUID,
            "fly_workspaces"
          );
          const storesQuery = query(
            storesRef,
            where("admin", "==", this.WID),
            where("storeWorkspace", "==", true)
          );
          const storesSnapshot = await getDocs(storesQuery);

          const stores = storesSnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));

          if (stores.length > 0) {
            console.log("Fetched stores from Firestore:", stores.length);
            // Update the Vuex store
            this.$store.commit("SET_GLOBAL_STORES", stores);
            // Try to get workspace data from fetched stores
            const workspaceFromStores = stores.find(store => store.id === this.WID);
            if (workspaceFromStores) {
              console.log("Using workspace data from fetched stores:", workspaceFromStores);
              this.workspaceData = workspaceFromStores;
              this.storeLocatorStyles = workspaceFromStores.store_locator_styles || {};
            } else {
              // If not found in fetched stores, fetch from Firestore
              await this.fetchWorkspaceData();
            }
            this.processStores(stores);
          } else {
            console.log("No stores found in Firestore");
            this.locations = [];
            this.locationTree = {};
            // Try to fetch workspace data as last resort
            await this.fetchWorkspaceData();
          }
        }
      } catch (error) {
        console.error("Error fetching locations:", error);
        this.locations = [];
        this.locationTree = {};
      } finally {
        this.isLoading = false;
      }
    },

    processStores(stores) {
      console.log("Processing stores for tree view:", stores.length);
      
      // Process store data similar to Location.vue
      const locations = stores.map((storeData) => {
        let locationData = { ...storeData };
        locationData.phone = locationData?.gmb_shallow_profile_data
          ? locationData?.gmb_shallow_profile_data?.phone
          : locationData?.gmbProfile?.phoneNumbers?.primaryPhone;
        locationData.website = locationData?.gmb_shallow_profile_data
          ? locationData?.gmb_shallow_profile_data?.website
          : locationData?.gmbProfile?.websiteUri;
        locationData.averageRating = locationData?.gmb_shallow_profile_data
          ? locationData?.gmb_shallow_profile_data?.rating
          : locationData.averageRating || 0;
        locationData.reviewCount = locationData?.gmb_shallow_profile_data
          ? locationData?.gmb_shallow_profile_data?.reviews
          : locationData.reviewCount || 0;
        locationData.profile_strength = this.calculateProfileStrength(locationData);
        locationData.name = locationData?.gmb_shallow_profile_data
          ? locationData?.gmb_shallow_profile_data?.title
          : locationData?.gmbProfile?.title || locationData.name || "";

        // Add google_business_profile to connected_apps if GMB data exists
        if (
          (locationData.gmb_shallow_profile_data || locationData.gmbProfile) &&
          (!locationData.connected_apps ||
            !locationData.connected_apps.includes("google_business_profile"))
        ) {
          if (!locationData.connected_apps) {
            locationData.connected_apps = [];
          }
          if (!locationData.connected_apps.includes("google_business_profile")) {
            locationData.connected_apps.push("google_business_profile");
          }
        }

        return locationData;
      });

      this.locations = locations;
      this.buildLocationTree(locations);
      this.fetchAllPlatformLogos();
    },

    buildLocationTree(locations) {
      const tree = {};

      locations.forEach(location => {
        const country = location.country || 'Other Countries';
        const state = location.state || 'Other States';

        if (!tree[country]) {
          tree[country] = {
            states: {},
            totalCount: 0
          };
        }

        if (!tree[country].states[state]) {
          tree[country].states[state] = {
            locations: []
          };
        }

        tree[country].states[state].locations.push(location);
        tree[country].totalCount++;
      });

      // Sort locations within each state by name
      Object.values(tree).forEach(countryData => {
        Object.values(countryData.states).forEach(stateData => {
          stateData.locations.sort((a, b) => {
            const nameA = (a.name || a.storeLocation || '').toLowerCase();
            const nameB = (b.name || b.storeLocation || '').toLowerCase();
            return nameA.localeCompare(nameB);
          });
        });
      });

      this.locationTree = tree;
    },

    calculateProfileStrength(location) {
      const gmbProfile = location.gmbProfile;
      const shallowProfile = location.gmb_shallow_profile_data;
      
      const fields = {
        description: !!gmbProfile?.profile?.description,
        websiteUri: shallowProfile
          ? !!shallowProfile?.website
          : !!gmbProfile?.websiteUri,
        primaryCategory: shallowProfile
          ? !!shallowProfile?.primary_category
          : !!gmbProfile?.categories?.primaryCategory?.displayName,
        additionalCategories: shallowProfile
          ? !!(shallowProfile?.additional_categories?.length > 0)
          : !!(gmbProfile?.categories?.additionalCategories?.length > 0),
        regularHours: shallowProfile
          ? !!(shallowProfile?.opening_hours?.length > 0)
          : !!(gmbProfile?.regularHours?.periods?.length > 0),
        phoneNumber: shallowProfile
          ? !!shallowProfile?.phone
          : !!gmbProfile?.phoneNumbers?.primaryPhone,
        openDate: shallowProfile
          ? !!shallowProfile?.opening_date
          : !!gmbProfile?.openInfo?.openingDate,
        postCount: shallowProfile
          ? (shallowProfile?.post_count || 0) > 0
          : (gmbProfile?.postCount || 0) > 0,
        hasLogo: shallowProfile
          ? !!shallowProfile?.logo
          : !!gmbProfile?.mediaItems?.logo,
        hasCoverPhoto: shallowProfile
          ? !!shallowProfile?.cover_photo
          : !!gmbProfile?.mediaItems?.coverPhoto,
        hasPhotos: shallowProfile
          ? (shallowProfile?.photos_length || 0) > 0
          : (gmbProfile?.mediaItems?.photosLength || 0) > 0,
        attributeCount: shallowProfile
          ? (shallowProfile?.attribute_count || 0) > 0
          : (gmbProfile?.attributeCount || 0) > 0,
        hasSocialLinks: shallowProfile
          ? shallowProfile?.has_social_links === true
          : gmbProfile?.socialInfo?.found === true,
      };

      const totalFields = Object.keys(fields).length;
      const filledFields = Object.values(fields).filter(Boolean).length;
      return Math.round((filledFields / totalFields) * 100);
    },

    async fetchPlatformLogo(platform) {
      if (this.platformLogoCache[platform]) {
        return;
      }

      try {
        const platformDoc = await getDoc(
          doc(db, "fly_platforms", platform.toLowerCase())
        );
        if (platformDoc.exists()) {
          const logoUrl = platformDoc.data().logo_url;
          this.$set(this.platformLogoCache, platform, logoUrl);
        }
      } catch (error) {
        console.error("Error fetching platform logo:", error);
      }
    },

    fetchAllPlatformLogos() {
      // Always pre-fetch Google Business Profile logo
      this.fetchPlatformLogo("google_business_profile");

      // Fetch logos for all connected apps across all locations
      this.locations.forEach((location) => {
        if (location.connected_apps && Array.isArray(location.connected_apps)) {
          location.connected_apps.forEach((platform) => {
            this.fetchPlatformLogo(platform);
          });
        }
      });
    },

    toggleCountry(country) {
      const index = this.expandedCountries.indexOf(country);
      if (index > -1) {
        this.expandedCountries.splice(index, 1);
      } else {
        this.expandedCountries.push(country);
      }
    },

    toggleState(country, state) {
      const stateKey = `${country}-${state}`;
      const index = this.expandedStates.indexOf(stateKey);
      if (index > -1) {
        this.expandedStates.splice(index, 1);
      } else {
        this.expandedStates.push(stateKey);
      }
    },

    selectLocation(location) {
      this.selectedLocation = location;
      // Expand treeview for selected location
      if (location) {
        const country = location.country || 'Other Countries';
        const state = location.state || 'Other States';
        if (!this.expandedCountries.includes(country)) {
          this.expandedCountries.push(country);
        }
        const stateKey = `${country}-${state}`;
        if (!this.expandedStates.includes(stateKey)) {
          this.expandedStates.push(stateKey);
        }
      }
      
      // Update URL with SEO-friendly store name (only if navigation is not disabled)
      if (location && (location.name || location.storeLocation) && !this.disableNavigation) {
        const storeName = location.name || location.storeLocation;
        // Slugify: replace spaces/special chars with hyphens, lowercase
        const slug = storeName
          .toString()
          .trim()
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '');
        
        // Use new URL format: <uid>/<wid>/<slug>
        const uid = this.embedUid || this.$route.params.uid;
        const wid = this.embedWid || this.$route.params.wid;
        if (uid && wid) {
          this.$router.replace({
            name: 'store-locator',
            params: { uid, wid, slug }
          });
        }
      }
    },

    getLocationStatus(location) {
      if (location.shallowConnect) return 'Shallow Connected';
      if (location.status === 'connected') return 'Connected';
      if (location.status === 'accepted') return 'Accepted';
      if (location.status === 'invited') return 'Invited';
      return 'Unknown';
    },

    getLocationStatusColor(location) {
      if (location.shallowConnect) return 'bg-blue-500';
      if (location.status === 'connected') return 'bg-green-500';
      if (location.status === 'accepted') return 'bg-yellow-500';
      if (location.status === 'invited') return 'bg-purple-500';
      return 'bg-gray-500';
    },

    getStatusBadgeClass(location) {
      if (location.shallowConnect) return 'bg-blue-50 text-blue-700 border border-blue-200';
      if (location.status === 'connected') return 'bg-green-50 text-green-700 border border-green-200';
      if (location.status === 'accepted') return 'bg-yellow-50 text-yellow-700 border border-yellow-200';
      if (location.status === 'invited') return 'bg-purple-50 text-purple-700 border border-purple-200';
      return 'bg-gray-50 text-gray-700 border border-gray-200';
    },

    getProfileHealthColor(score) {
      if (score >= 80) return 'bg-green-500';
      if (score >= 60) return 'bg-yellow-500';
      if (score >= 40) return 'bg-orange-500';
      return 'bg-red-500';
    },

    getFullAddress(location) {
      // Prefer full address fields if available
      if (location?.address) return location.address;
      if (location?.gmbProfile?.storefrontAddress?.addressLines?.length) {
        return location.gmbProfile.storefrontAddress.addressLines.join(', ');
      }
      if (location?.gmbProfile?.address) return location.gmbProfile.address;
      // Fallback to current logic, but include location name
      const parts = [
        location.name || location.storeLocation,
        location.sub_locality,
        location.city,
        location.state,
        location.zipcode,
        location.country
      ].filter(Boolean);
      return parts.join(', ');
    },

    refreshLocations() {
      this.fetchLocations();
    },

    // Methods for full profile view
    getBusinessDescription(location) {
      return location?.gmbProfile?.profile?.description || 
             location?.gmb_shallow_profile_data?.description || 
             null;
    },

    getCoverPhoto(location) {
      // For shallow profile data
      if (location?.gmb_shallow_profile_data?.cover_photo) {
        return location.gmb_shallow_profile_data.cover_photo;
      }
      
      // For deep profile data
      if (location?.gmbProfile?.mediaItems?.coverPhoto?.googleUrl) {
        return location.gmbProfile.mediaItems.coverPhoto.googleUrl;
      }
      
      return null;
    },

    getSocialLinks(location) {
      const socialLinks = [];
      
      // Check for social links in attributes (deep profile)
      if (location?.gmb_attributes && Array.isArray(location.gmb_attributes)) {
        const socialPlatforms = {
          'attributes/url_facebook': { platform: 'Facebook', icon: 'mdi-facebook', iconColor: '#1877F2' },
          'attributes/url_instagram': { platform: 'Instagram', icon: 'mdi-instagram', iconColor: '#E4405F' },
          'attributes/url_twitter': { platform: 'Twitter', icon: 'mdi-twitter', iconColor: '#1DA1F2' },
          'attributes/url_linkedin': { platform: 'LinkedIn', icon: 'mdi-linkedin', iconColor: '#0A66C2' },
          'attributes/url_pinterest': { platform: 'Pinterest', icon: 'mdi-pinterest', iconColor: '#BD081C' },
          'attributes/url_youtube': { platform: 'YouTube', icon: 'mdi-youtube', iconColor: '#FF0000' }
        };
        
        location.gmb_attributes.forEach(attr => {
          if (socialPlatforms[attr.name] && attr.value) {
            socialLinks.push({
              platform: socialPlatforms[attr.name].platform,
              url: attr.value,
              icon: socialPlatforms[attr.name].icon,
              iconColor: socialPlatforms[attr.name].iconColor
            });
          }
        });
      }
      
      // Check for social links in shallow profile data
      if (location?.gmb_shallow_profile_data?.social_links && Array.isArray(location.gmb_shallow_profile_data.social_links)) {
        location.gmb_shallow_profile_data.social_links.forEach(link => {
          if (link.url && link.platform) {
            const platformConfig = {
              'facebook': { icon: 'mdi-facebook', iconColor: '#1877F2' },
              'instagram': { icon: 'mdi-instagram', iconColor: '#E4405F' },
              'twitter': { icon: 'mdi-twitter', iconColor: '#1DA1F2' },
              'linkedin': { icon: 'mdi-linkedin', iconColor: '#0A66C2' },
              'pinterest': { icon: 'mdi-pinterest', iconColor: '#BD081C' },
              'youtube': { icon: 'mdi-youtube', iconColor: '#FF0000' }
            };
            
            const config = platformConfig[link.platform.toLowerCase()];
            if (config) {
              socialLinks.push({
                platform: link.platform.charAt(0).toUpperCase() + link.platform.slice(1),
                url: link.url,
                icon: config.icon,
                iconColor: config.iconColor
              });
            }
          }
        });
      }
      
      return socialLinks;
    },

    handleCoverPhotoError(event) {
      console.error('Cover photo failed to load:', event.target.src);
      // Set error flag to hide the cover photo panel
      this.coverPhotoError = true;
      this.coverPhotoLoading = false;
    },

    handleCoverPhotoLoad() {
      console.log("Cover photo loaded successfully");
      this.coverPhotoError = false;
      this.coverPhotoLoading = false;
    },

    getOpeningHours(location) {
      // For shallow profile data
      if (location?.gmb_shallow_profile_data?.opening_hours) {
        return location.gmb_shallow_profile_data.opening_hours.map(hours => ({
          day: this.formatDayName(hours.day),
          hours: hours.hours
        }));
      }
      
      // For deep profile data
      if (location?.gmbProfile?.regularHours?.periods) {
        const daysMap = {
          'MONDAY': 'Monday',
          'TUESDAY': 'Tuesday', 
          'WEDNESDAY': 'Wednesday',
          'THURSDAY': 'Thursday',
          'FRIDAY': 'Friday',
          'SATURDAY': 'Saturday',
          'SUNDAY': 'Sunday'
        };
        
        return location.gmbProfile.regularHours.periods.map(period => ({
          day: daysMap[period.openDay] || period.openDay,
          hours: period.openTime && period.closeTime ? 
                 `${period.openTime.hours}:${period.openTime.minutes?.toString().padStart(2, '0') || '00'} - ${period.closeTime.hours}:${period.closeTime.minutes?.toString().padStart(2, '0') || '00'}` : 
                 'Closed'
        }));
      }
      
      return [];
    },

    getPrimaryCategory(location) {
      return location?.gmb_shallow_profile_data?.primary_category ||
             location?.gmbProfile?.categories?.primaryCategory?.displayName ||
             'Business';
    },

    getAdditionalCategories(location) {
      if (location?.gmb_shallow_profile_data?.additional_categories) {
        return location.gmb_shallow_profile_data.additional_categories;
      }
      
      if (location?.gmbProfile?.categories?.additionalCategories) {
        return location.gmbProfile.categories.additionalCategories.map(cat => cat.displayName);
      }
      
      return [];
    },

    formatDayName(day) {
      const daysMap = {
        'monday': 'Monday',
        'tuesday': 'Tuesday',
        'wednesday': 'Wednesday', 
        'thursday': 'Thursday',
        'friday': 'Friday',
        'saturday': 'Saturday',
        'sunday': 'Sunday'
      };
      return daysMap[day?.toLowerCase()] || day;
    },

    formatWebsite(website) {
      if (!website) return '';
      return website.replace(/^https?:\/\//, '').replace(/^www\./, '');
    },

    isCurrentDay(day) {
      const today = new Date().toLocaleDateString('en-US', { weekday: 'long' });
      return day?.toLowerCase() === today.toLowerCase();
    },

    callBusiness() {
      if (this.selectedLocation?.phone) {
        window.location.href = `tel:${this.selectedLocation.phone}`;
      }
    },

    visitWebsite() {
      if (this.selectedLocation?.website) {
        window.open(this.selectedLocation.website, '_blank');
      }
    },

    async fetchApifyLatLng(locationId) {
      try {
        console.log('[fetchApifyLatLng] Fetching Apify lat/lng for locationId:', locationId);
        // Only use the subcollection with random doc IDs
          const apifyColRef = collection(db, "users", this.currentUID, "fly_workspaces", locationId, "gmb_locations_apify_profiles");
          const apifyColSnap = await getDocs(apifyColRef);
        console.log('[fetchApifyLatLng] Documents found:', apifyColSnap.docs.length);
        let found = false;
          for (const docSnap of apifyColSnap.docs) {
            const data = docSnap.data();
          console.log('[fetchApifyLatLng] Checking doc:', docSnap.id, data);
          if (data && data.location && typeof data.location.lat === 'number' && typeof data.location.lng === 'number') {
            this.apifyLat = data.location.lat;
            this.apifyLng = data.location.lng;
            console.log('[fetchApifyLatLng] Found lat/lng:', this.apifyLat, this.apifyLng);
            found = true;
              break;
            }
          }
        if (!found) {
          this.apifyLat = null;
          this.apifyLng = null;
          console.log('[fetchApifyLatLng] No lat/lng found in any Apify profile doc.');
        }
      } catch (e) {
        this.apifyLat = null;
        this.apifyLng = null;
        console.error('[fetchApifyLatLng] Error:', e);
      }
    },

    handleLogoError() {
      console.error("❌ [BRAND LOGO] Logo image failed to load");
      console.error("❌ [BRAND LOGO] Failed logo URL:", this.storeLogo);
      this.logoLoadError = true;
    },

    handleLogoLoad() {
      console.log("✅ [BRAND LOGO] Logo image loaded successfully");
      console.log("✅ [BRAND LOGO] Successfully loaded logo URL:", this.storeLogo);
      this.logoLoaded = true;
    },

    updateFavicon() {
      // Don't update favicon if disabled
      if (this.disableFavicon) {
        console.log("🚫 [BRAND LOGO] Favicon updates disabled");
        return;
      }
      
      // Only update favicon if we have a valid logo URL (not the default SVG)
      const logoUrl = this.storeLogo;
      const defaultSvg = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNMTIgMkM2LjQ4IDIgMiA2LjQ4IDIgMTJzNC40OCAxMCAxMCAxMCAxMC00LjQ4IDEwLTEwUzE3LjUyIDIgMTIgMnptMCAxOGMtNC40MSAwLTgtMy41OS04LThzMy41OS04IDgtOCA4IDMuNTkgOCA4LTMuNTkgOC04IDh6IiBmaWxsPSIjZTVlN2ViIi8+PC9zdmc+';
      
      if (!logoUrl || logoUrl === defaultSvg) {
        console.log("🚫 [BRAND LOGO] No valid logo URL available for favicon");
        console.log("🚫 [BRAND LOGO] Logo URL:", logoUrl);
        console.log("🚫 [BRAND LOGO] Is default SVG:", logoUrl === defaultSvg);
        return;
      }

      console.log("🔄 [BRAND LOGO] Updating favicon with logo:", logoUrl);

      // Remove existing favicon links
      const existingFavicons = document.querySelectorAll("link[rel~='icon']");
      existingFavicons.forEach(link => {
        if (link.href !== this._originalFaviconHref) {
          link.remove();
        }
      });

      // Create new favicon link
      const link = document.createElement('link');
      link.rel = 'icon';
      link.href = logoUrl;
      link.type = 'image/x-icon';
      document.head.appendChild(link);

      console.log("✅ [BRAND LOGO] Favicon updated successfully with URL:", logoUrl);
    },

    updatePageTitle() {
      // Don't update page title if disabled
      if (this.disablePageTitle) {
        console.log("Page title updates disabled");
        return;
      }
      
      if (this.brandName && this.brandName !== 'Loading...') {
        let newTitle = `${this.brandName} - Store Locator`;
        
        // Add location count if available
        if (this.totalLocations > 0) {
          newTitle += ` (${this.totalLocations} locations)`;
        }
        
        console.log("Updating page title to:", newTitle);
        document.title = newTitle;
      }
    },

    selectHeaderTab(tabValue) {
      // If the tab value looks like a URL, open it in a new tab
      if (typeof tabValue === 'string' && /^https?:\/\//.test(tabValue)) {
        window.open(tabValue, '_blank');
        return;
      }
      this.selectedTab = tabValue;
      this.$emit('tab-change', tabValue);
    },

    getDirections() {
      // Prefer coordinates if available
      if (this.apifyLat && this.apifyLng) {
        const url = `https://www.google.com/maps/dir/?api=1&destination=${this.apifyLat},${this.apifyLng}`;
        window.open(url, '_blank');
      } else if (this.selectedLocation) {
        const address = encodeURIComponent(this.getFullAddress(this.selectedLocation));
        const url = `https://www.google.com/maps/dir/?api=1&destination=${address}`;
        window.open(url, '_blank');
      }
    },

    getGoogleMapsSrc() {
      console.log('[getGoogleMapsSrc] Called. apifyLat:', this.apifyLat, 'apifyLng:', this.apifyLng);
      if (this.apifyLat && this.apifyLng) {
        const url = `https://www.google.com/maps?q=${this.apifyLat},${this.apifyLng}&output=embed`;
        console.log('[getGoogleMapsSrc] Using lat/lng URL:', url);
        return url;
      }
      if (this.selectedLocation) {
        // Include shop name in the address string for Google Maps
        const addrParts = [
          this.selectedLocation.name || this.selectedLocation.storeLocation,
          this.getFullAddress(this.selectedLocation)
        ].filter(Boolean);
        const addr = addrParts.join(', ');
        const url = `https://www.google.com/maps?q=${encodeURIComponent(addr)}&output=embed`;
        console.log('[getGoogleMapsSrc] Using address string URL:', url);
        return url;
      }
      console.log('[getGoogleMapsSrc] No lat/lng or address available, returning empty string.');
      return '';
    },

    async fetchReviewsForLocation(location) {
      this.isReviewsLoading = true;
      this.reviews = [];
      // Reset pagination when fetching new reviews
      this.reviewsPage = 1;
      try {
        // Prepare account info for the selected location
        const currentUID = this.currentUID;
        const wid = location.id;
        // Get workspace details
        const workspaceRef = doc(db, "users", currentUID, "fly_workspaces", wid);
        const workspaceDoc = await getDoc(workspaceRef);
        let isShallow = false;
        let placeId = workspaceDoc.data()?.placeId;
        if (workspaceDoc.exists() && workspaceDoc.data()?.shallowConnect) {
          isShallow = true;
        }
        let accounts = [];
        if (isShallow) {
          accounts.push({
            uid: currentUID,
            wid,
            location: placeId,
            isShallow: true
          });
        } else {
          // For regular workspaces, check for GMB connection
          const connectedAppsRef = collection(db, "users", currentUID, "fly_workspaces", wid, "connected-apps");
          const gbpSnapshot = await getDocs(query(connectedAppsRef, where("__name__", "==", "google_business_profile")));
          if (!gbpSnapshot.empty) {
            const gbpData = gbpSnapshot.docs[0].data();
            const accountId = gbpData.accounts?.[0]?.id || "";
            (gbpData.accounts || []).forEach((account) => {
              const locationId = account.locations?.[0]?.name || "";
              accounts.push({
                uid: currentUID,
                wid,
                location: locationId,
                account: accountId,
                isShallow: false
              });
            });
          }
        }
        if (accounts.length === 0) {
          this.reviews = [];
          return;
        }
        const payload = {
          uid: currentUID,
          accounts,
          action: "get_gmb_reviews",
          platform: "google_business_profile",
          page: 1,
          pageSize: 10, // Limit to 10 reviews
        };
        console.log('[fetchReviewsForLocation] Payload:', payload);
        let idToken = null;
        if (auth.currentUser) {
          idToken = await auth.currentUser.getIdToken();
        }
        const headers = {
          "Content-Type": "application/json",
        };
        if (idToken) {
          headers["Authorization"] = `Bearer ${idToken}`;
        }
        console.log('[fetchReviewsForLocation] ID Token:', idToken);
        console.log('[fetchReviewsForLocation] Endpoint:', `${process.env.VUE_APP_ENGINE_ENDPOINT}/functions/GmbReviewsManagement`);
        const response = await fetch(
          `${process.env.VUE_APP_ENGINE_ENDPOINT}/functions/GmbReviewsManagement`,
          {
            method: "POST",
            headers,
            body: JSON.stringify({ data: payload }),
          }
        );
        console.log('[fetchReviewsForLocation] Response:', response);
        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            const reviews = result?.data?.reviews || [];
            const ratingMap = { ONE: 1, TWO: 2, THREE: 3, FOUR: 4, FIVE: 5 };
            const now = new Date();
            const oneMonthAgo = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
            this.reviews = reviews
              .map((review) => {
                return {
                  reviewId: review.reviewId,
                  displayName: review.reviewer?.displayName || "Anonymous",
                  profilePhotoUrl: review.reviewer?.profilePhotoUrl || 'https://www.gravatar.com/avatar/?d=mp',
                  starRating: ratingMap[review.starRating] || 0,
                  comment: review.comment || "",
                  updateTime: review.updateTime,
                };
              })
              .filter((review) => {
                // Only include reviews from the past month
                if (!review.updateTime) return false;
                const reviewDate = new Date(review.updateTime);
                return reviewDate >= oneMonthAgo && reviewDate <= now;
              })
              .slice(0, 10); // Ensure we only keep the latest 10 reviews
          }
        }
        console.log('[fetchReviewsForLocation] Reviews:', this.reviews);
      } catch (e) {
        this.reviews = [];
      } finally {
        this.isReviewsLoading = false;
      }
    },
    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },

    async fetchPostsForLocation(location) {
      this.isPostsLoading = true;
      this.posts = [];
      // Reset expanded posts state when fetching new posts
      this.expandedPosts = {};
      try {
        if (!location || !location.id) {
          console.log('[fetchPostsForLocation] No location or location ID provided');
          return;
        }

        console.log('[fetchPostsForLocation] Fetching posts for location:', location.id);
        console.log('[fetchPostsForLocation] Using workspace ID:', this.WID);
        
        // Calculate date range for last month
        const now = new Date();
        const oneMonthAgo = new Date(new Date().setDate(new Date().getDate() - 30)); // 30 days ago
        
        console.log('[fetchPostsForLocation] Date range - Start:', oneMonthAgo, 'End:', now);
        
        // Use the public fetchSocialPosts Cloud Function instead of direct Firestore access
        const fetchSocialPosts = httpsCallable(functions, "fetchSocialPosts");
        
        const payload = {
          uid: this.currentUID,
          wid: location.id, // Pass the specific location ID to filter posts
          operation: "fetchDateRangePosts", // Use the correct operation name
          startDate: oneMonthAgo, // Use Date object instead of ISO string
          endDate: now, // Use Date object instead of ISO string
          limit: 10
        };
        
        console.log('[fetchPostsForLocation] Payload:', payload);
        
        const response = await fetchSocialPosts(payload);
        console.log('[fetchPostsForLocation] Response:', response);
        
        if (response.data && response.data.result) {
          const fetchedPosts = response.data.result;
          
          // Get platform data for social media icons
          const platformsRef = collection(db, "fly_platforms");
          const platformsSnapshot = await getDocs(platformsRef);
          const platforms = {};
          platformsSnapshot.forEach(doc => {
            platforms[doc.id] = doc.data();
          });

          // Process posts to add platform information and ensure unique IDs
          this.posts = fetchedPosts.map((post, index) => {
            const socialMediaArr = [];
            if (post.social_media && Array.isArray(post.social_media)) {
              post.social_media.forEach(media => {
                const platformName = typeof media === 'string' ? media : media.name;
                const platformData = platforms[platformName];
                if (platformData) {
                  socialMediaArr.push({
                    name: platformName,
                    label: platformData.name,
                    logo_url: platformData.logo_url,
                  });
                }
              });
            }
            
            // Ensure each post has a unique ID
            const uniqueId = post.id || `${location.id}_post_${index}_${Date.now()}`;
            
            return {
              ...post,
              id: uniqueId, // Ensure unique ID
              social_media: socialMediaArr
            };
          });

          console.log('[fetchPostsForLocation] Fetched posts:', this.posts.length);
        } else {
          console.log('[fetchPostsForLocation] No posts data in response');
          this.posts = [];
        }
      } catch (error) {
        console.error('[fetchPostsForLocation] Error fetching posts:', error);
        // Fallback: try to get posts from location data if available
        if (location.recent_posts && Array.isArray(location.recent_posts)) {
          console.log('[fetchPostsForLocation] Using fallback posts from location data');
          this.posts = location.recent_posts;
        } else {
          this.posts = [];
        }
      } finally {
        this.isPostsLoading = false;
      }
    },

    // Add pagination methods for reviews
    nextReviewPage() {
      if (this.hasNextReviewPage) {
        this.reviewsPage++;
      }
    },

    prevReviewPage() {
      if (this.hasPrevReviewPage) {
        this.reviewsPage--;
      }
    },

    toggleReadMore(postId) {
      console.log('[toggleReadMore] Toggling post ID:', postId);
      console.log('[toggleReadMore] Current expandedPosts state:', this.expandedPosts);
      console.log('[toggleReadMore] Current state for this post:', this.expandedPosts[postId]);
      
      // Use Vue.set to ensure reactivity
      this.$set(this.expandedPosts, postId, !this.expandedPosts[postId]);
      
      console.log('[toggleReadMore] New state for this post:', this.expandedPosts[postId]);
      console.log('[toggleReadMore] Updated expandedPosts state:', this.expandedPosts);
    },

    // SEO Methods
    updateSEO() {
      console.log('[StoreLocator] Updating SEO meta tags');
      
      if (!this.workspaceData) {
        console.log('[StoreLocator] No workspace data available for SEO');
        return;
      }

      // Generate SEO content
      const title = generateStoreLocatorTitle(this.workspaceData, this.totalLocations);
      const description = generateStoreLocatorDescription(this.workspaceData, this.totalLocations);
      const keywords = this.generateStoreLocatorKeywords();
      const image = this.getSEOImage();
      const url = this.getCurrentURL();

      console.log('[StoreLocator] SEO content generated:', { title, description, keywords, image, url });

      // Update meta tags
      updateMetaTags({
        title,
        description,
        keywords,
        image,
        url,
        type: 'website',
        siteName: this.brandName
      });

      // Generate and inject structured data
      const structuredData = generateStoreLocatorStructuredData(this.workspaceData, this.locations);
      injectStructuredData(structuredData);

      console.log('[StoreLocator] SEO meta tags and structured data updated');
    },

    generateStoreLocatorKeywords() {
      const keywords = [];
      
      // Add brand name
      if (this.brandName && this.brandName !== 'Loading...') {
        keywords.push(this.brandName);
      }
      
      // Add location-related keywords
      keywords.push('store locator', 'find locations', 'business locations', 'store finder');
      
      // Add location cities if available
      const cities = this.uniqueCities.slice(0, 5); // Limit to first 5 cities
      keywords.push(...cities);
      
      // Add business category if available
      if (this.locations.length > 0) {
        const firstLocation = this.locations[0];
        const category = firstLocation?.gmbProfile?.categories?.primaryCategory?.displayName ||
                         firstLocation?.gmb_shallow_profile_data?.primary_category;
        if (category) {
          keywords.push(category);
        }
      }
      
      return keywords.join(', ');
    },

    getSEOImage() {
      // Use workspace logo as OG image
      const logo = this.storeLogo;
      if (logo && logo !== 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNMTIgMkM2LjQ4IDIgMiA2LjQ4IDIgMTJzNC40OCAxMCAxMCAxMCAxMC00LjQ4IDEwLTEwUzE3LjUyIDIgMTIgMnptMCAxOGMtNC40MSAwLTgtMy41OS04LThzMy41OS04IDgtOCA4IDMuNTkgOCA4LTMuNTkgOC04IDh6IiBmaWxsPSIjZTVlN2ViIi8+PC9zdmc+') {
        return logo;
      }
      
      // Fallback to cover photo from first location
      if (this.locations.length > 0) {
        const firstLocation = this.locations[0];
        return this.getCoverPhoto(firstLocation);
      }
      
      return null;
    },

    getCurrentURL() {
      // Get current URL for canonical tag
      if (typeof window !== 'undefined') {
        return window.location.href;
      }
      return '';
    },
  },

  mounted() {
    console.log("StoreLocator component mounted with props:", {
      embedUid: this.embedUid,
      embedWid: this.embedWid,
      embedSlug: this.embedSlug,
      WID: this.WID,
      currentUID: this.currentUID
    });

    // Check if PostCard component is available
    console.log('[StoreLocator] PostCard component check:', {
      PostCard: typeof PostCard,
      PostCardName: PostCard?.name,
      PostCardOptions: PostCard?.options
    });

    if (
      (auth.currentUser && auth.currentUser.uid !== this.currentUID) ||
      this.currentWorkspace?.storeWorkspace
    ) {
      this.$snackbar.show("Direct access is prohibited");
      this.$router.push("/calendar");
      return;
    }

    // Store original favicon reference
    const favicon = document.querySelector("link[rel~='icon']");
    if (favicon) {
      this._originalFaviconHref = favicon.href;
    }

    // Store original page title
    this._originalPageTitle = document.title;

    // Fetch workspace data and locations
    this.fetchLocations().then(() => {
      console.log('[StoreLocator] Locations fetched, checking for auto-selection...');
      
      // Handle slug-based selection for new URL format
      if (this.embedSlug && !this.selectedLocation && this.locations && this.locations.length > 0) {
        this.selectLocationBySlug(this.embedSlug);
      }
      
      // Auto-select location based on slug in URL (legacy support)
      const slug = this.$route.params.slug;
      if (slug && !this.selectedLocation && this.locations && this.locations.length > 0) {
        const slugify = str => str && str.toString().trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
        const match = this.locations.find(loc => slugify(loc.name || loc.storeLocation) === slug);
        if (match) {
          console.log('[StoreLocator] Auto-selecting location based on slug:', match);
          this.selectedLocation = match;
          // Expand treeview for selected location
          const country = match.country || 'Other Countries';
          const state = match.state || 'Other States';
          if (!this.expandedCountries.includes(country)) {
            this.expandedCountries.push(country);
          }
          const stateKey = `${country}-${state}`;
          if (!this.expandedStates.includes(stateKey)) {
            this.expandedStates.push(stateKey);
          }
        }
      }
      
      // Update SEO after data is loaded
      if (this.workspaceData && this.locations.length > 0) {
        console.log('[StoreLocator] Initial SEO update');
        this.updateSEO();
      }
    });

    // Set default selected tab
    if (this.headerTabs.length && !this.selectedTab) {
      this.selectedTab = this.headerTabs[0].value;
    }
  },
  beforeDestroy() {
    // Restore original favicon
    if (this._originalFaviconHref) {
      const favicon = document.querySelector("link[rel~='icon']");
      if (favicon) {
        favicon.href = this._originalFaviconHref;
        console.log("Restored original favicon:", this._originalFaviconHref);
      }
    }
    
    // Clean up any custom favicon links we created
    const customFavicons = document.querySelectorAll("link[rel~='icon']");
    customFavicons.forEach(link => {
      if (link.href !== this._originalFaviconHref) {
        link.remove();
        console.log("Removed custom favicon link");
      }
    });

    // Restore original page title
    if (this._originalPageTitle) {
      console.log("Restoring original page title:", this._originalPageTitle);
      document.title = this._originalPageTitle;
    }
    
    // Clean up SEO meta tags
    console.log('[StoreLocator] Cleaning up SEO meta tags');
    cleanupMetaTags();
  },
};
</script>

<style scoped>
:root {
  --store-primary: #1773EA;
  --store-secondary: #6B7280;
  --store-panel-bg: linear-gradient(90deg, #eff6ff 0%, #e0e7ff 100%);
  --store-header-text: #222;
  --store-body-text: #333;
  --store-border-radius: 1.25rem;
  --store-font-family: Inter, sans-serif;
  --store-button-bg: #1773EA;
  --store-button-text: #fff;
  --store-selected-bg: rgba(23, 115, 234, 0.1);
  --store-selected-border: #1773EA;
  --store-selected-text: #1773EA;
  --store-logo-size-mobile: 2.25rem;
  --store-logo-size-desktop: 3rem;
  --store-header-bg: #fff;
  --store-tab-text: #333;
  --store-tab-hover-text: #1773EA;
  --store-tab-selected-text: #fff;
  --store-tab-bg: #f3f4f6;
  --store-tab-hover-bg: #e0e7ff;
  --store-tab-selected-bg: #1773EA;
  --store-search-border: #bdb4e7;
  --store-search-radius: 0.75rem;
  --store-search-border-size: 2px;
  --store-brand-name-size: 1.375rem;
  --store-brand-name-color: #000000;
  --sidepanel-selected-bg: rgba(23, 115, 234, 0.15);
  --sidepanel-selected-text: rgba(23, 115, 234, 1);
  --sidepanel-selected-border: rgba(23, 115, 234, 1);
  --sidepanel-selected-radius: 1.25rem;
  --sidepanel-hover-bg: rgba(23, 115, 234, 0.08);
  --sidepanel-hover-text: rgba(23, 115, 234, 1);
  --sidepanel-hover-border: rgba(23, 115, 234, 1);
  --sidepanel-hover-border-size: 2px;
  --sidepanel-hover-radius: 1.25rem;
  --sidepanel-unselected-bg: #fff;
  --sidepanel-unselected-text: #222;
  --sidepanel-unselected-border: transparent;
  --sidepanel-unselected-border-size: 1px;
  --sidepanel-unselected-radius: 0.75rem;
  --sidepanel-icon: rgba(23, 115, 234, 1);
  --sidepanel-chevron: rgba(23, 115, 234, 0.7);
  --sidepanel-selected-border-size: 2px;
  --details-address-bg: #f0f0f0;
  --details-address-text: #222;
  --details-address-border: #1773EA;
  --details-address-border-size: 2px;
  --details-address-radius: 1.25rem;
  --details-contact-bg: #f0f0f0;
  --details-contact-text: #222;
  --details-contact-border: #1773EA;
  --details-contact-border-size: 2px;
  --details-contact-radius: 1.25rem;
  --details-description-bg: #f0f0f0;
  --details-description-text: #222;
  --details-description-border: #1773EA;
  --details-description-border-size: 2px;
  --details-description-radius: 1.25rem;
  --details-cover-photo-bg: #f0f0f0;
  --details-cover-photo-text: #222;
  --details-cover-photo-border: #1773EA;
  --details-cover-photo-border-size: 2px;
  --details-cover-photo-radius: 1.25rem;
  --details-social-links-bg: #f0f0f0;
  --details-social-links-text: #222;
  --details-social-links-border: #1773EA;
  --details-social-links-border-size: 2px;
  --details-social-links-radius: 1.25rem;
  --details-hours-bg: #f0f0f0;
  --details-hours-text: #222;
  --details-hours-border: #1773EA;
  --details-hours-border-size: 2px;
  --details-hours-radius: 1.25rem;
  --details-categories-bg: #f0f0f0;
  --details-categories-text: #222;
  --details-categories-border: #1773EA;
  --details-categories-border-size: 2px;
  --details-categories-radius: 1.25rem;
  --details-posts-bg: #f0f0f0;
  --details-posts-text: #222;
  --details-posts-border: #1773EA;
  --details-posts-border-size: 2px;
  --details-posts-radius: 1.25rem;
  --details-actions-bg: #f0f0f0;
  --details-actions-text: #222;
  --details-actions-border: #1773EA;
  --details-actions-border-size: 2px;
  --details-actions-radius: 1.25rem;
  --details-btn-text: #fff;
  --details-btn-bg: #1773EA;
  --details-btn-border: #1773EA;
  --details-btn-radius: 0.75rem;
  --details-btn-border-size: 1.5px;
  --details-btn-hover-text: #fff;
  --details-btn-hover-bg: #125bb5;
  --details-btn-hover-border: #125bb5;
  --details-btn-hover-border-size: 1.5px;
  --details-btn-hover-border-radius: 0.75rem;
  --details-btn-selected-text: #fff;
  --details-btn-selected-bg: #125bb5;
  --details-btn-selected-border: #125bb5;
  --details-btn-selected-border-size: 1.5px;
  --details-btn-selected-border-radius: 0.75rem;
  --details-header-bg: #f0f0f0;
  --details-header-text: #222;
  --details-header-border: #1773EA;
  --details-header-border-size: 2px;
  --details-header-radius: 1.25rem;
  --details-primary: #1976d2;
  --details-secondary: #424242;
  --details-accent: #82B1FF;
  --details-info: #2196F3;
  --details-success: #4CAF50;
  --details-warning: #FFC107;
  --details-error: #FF5252;
  --details-posts-border-size: 2px;
  --details-posts-radius: 1.25rem;
  --details-actions-bg: #f0f0f0;
  --details-actions-text: #222;
  --details-actions-border: #1773EA;
  --details-actions-border-size: 2px;
  --details-actions-radius: 1.25rem;
  --details-reviews-bg: #f0f0f0;
  --details-reviews-text: #222;
  --details-reviews-border: #1773EA;
  --details-reviews-border-size: 2px;
  --details-reviews-radius: 1.25rem;
  --details-btn-text: #fff;
}
.bg-gradient-to-r {
  background: var(--store-panel-bg) !important;
}
.rounded-2xl {
  border-radius: var(--store-border-radius) !important;
}
.text-gray-800, .text-gray-900, h2, h3, h1 {
  color: var(--store-header-text) !important;
}
body, .font-sans, .store-locator-root {
  font-family: var(--store-font-family) !important;
}
.v-icon, .text-primary {
  color: var(--store-primary) !important;
}
button, .v-btn {
  background: var(--store-button-bg) !important;
  color: var(--store-button-text) !important;
}
.text-gray-700, .text-gray-500, p, label, span, div {
  color: var(--store-body-text);
}
.search-input {
  transition: border-color 0.3s, box-shadow 0.3s, background 0.2s;
  background: #fff;
  border: var(--store-search-border-size) solid var(--store-search-border);
  border-radius: var(--store-search-radius);
  color: var(--store-search-text);
}
.search-input input {
  color: var(--store-search-text) !important;
}
.search-input input::placeholder {
  color: var(--store-search-placeholder) !important;
  opacity: 1;
}
.search-input .v-icon,
.search-input .v-input__icon--prepend .v-icon,
.search-input .v-field__prepend-inner .v-icon {
  color: var(--store-search-icon) !important;
}
.search-input:hover {
  background: #f3f4f6;
  border-color: var(--store-search-border);
}
.search-input:focus {
  border-color: var(--store-primary);
  box-shadow: 0 0 0 2px #e9d8fd;
  background: #fff;
  outline: none;
}

.rotate-90 {
  transform: rotate(90deg);
  transition: transform 0.2s ease;
}

/* Scrollbar styling */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Smooth transitions */
.transition-colors {
  transition: background-color 0.2s ease, color 0.2s ease;
}

/* Responsive adjustments */
@media (max-width: 1024px) {
  .grid.lg\\:grid-cols-3 {
    grid-template-columns: 1fr;
  }
  
  .lg\\:col-span-1 {
    border-right: none;
    border-bottom: 1px solid #e5e7eb;
  }
  
  .max-h-65vh {
    max-height: 40vh;
  }
}

@media (max-width: 768px) {
  .search-input {
    width: 100%;
  }
  
  .grid.md\\:grid-cols-2 {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 900px) {
  .flex-col.md\:flex-row {
    flex-direction: column !important;
  }
  .w-full.md\:w-1\/2 {
    width: 100% !important;
  }
  .h-56.md\:h-48 {
    height: 220px !important;
  }
}

.bg-gradient-to-r {
  background: linear-gradient(90deg, #eff6ff 0%, #e0e7ff 100%);
}
.bg-white\/90 {
  background: rgba(255,255,255,0.90);
}
.rounded-2xl {
  border-radius: 1.25rem;
}
.shadow-lg {
  box-shadow: 0 6px 24px 0 rgba(80, 80, 120, 0.12);
}
.shadow-md {
  box-shadow: 0 2px 8px 0 rgba(80, 80, 120, 0.10);
}

/* Ensure flex children in address panel header do not overflow */
.address-header,
.address-header > * {
  max-width: 100%;
}

.location-selector-item {
  background: var(--sidepanel-unselected-bg) !important;
  color: var(--sidepanel-unselected-text) !important;
  border: var(--sidepanel-unselected-border-size) solid var(--sidepanel-unselected-border) !important;
  border-radius: var(--sidepanel-unselected-radius) !important;
  transition: background-color 0.2s, color 0.2s, border 0.2s, border-radius 0.2s;
}
.location-selector-item:not(.location-selected):hover {
  background: var(--sidepanel-hover-bg) !important;
  color: var(--sidepanel-hover-text) !important;
  border: var(--sidepanel-hover-border-size) solid var(--sidepanel-hover-border) !important;
  border-radius: var(--sidepanel-hover-radius) !important;
}
.location-selector-item:not(.location-selected):hover p,
.location-selector-item:not(.location-selected):hover span,
.location-selector-item:not(.location-selected):hover div {
  color: var(--sidepanel-hover-text) !important;
}
.location-selector-item:not(.location-selected):hover .v-icon {
  color: var(--sidepanel-hover-text) !important;
}
.location-selected {
  background: var(--sidepanel-selected-bg) !important;
  color: var(--sidepanel-selected-text) !important;
  border: var(--sidepanel-selected-border-size) solid var(--sidepanel-selected-border) !important;
  border-radius: var(--sidepanel-selected-radius) !important;
}
.location-selected .v-icon {
  color: var(--sidepanel-selected-text) !important;
}
.location-selected p {
  color: var(--sidepanel-selected-text) !important;
}
.v-icon {
  color: var(--sidepanel-icon) !important;
}
.chevron-icon {
  color: var(--sidepanel-chevron) !important;
}
.current-day {
  background-color: color-mix(in srgb, var(--details-primary) 15%, transparent) !important;
  border-color: var(--details-primary) !important;
}
.dynamic-logo {
  max-height: var(--store-logo-size-mobile) !important;
  max-width: 100%;
  height: auto !important;
  width: auto !important;
  display: block;
}

@media (min-width: 768px) {
  .dynamic-logo {
    max-height: var(--store-logo-size-desktop) !important;
  }
}

.header-bar {
  background: var(--store-header-bg) !important;
}

.header-tabs {
  background: transparent;
}
.header-tab {
  background: var(--store-tab-bg) !important;
  color: var(--store-tab-text) !important;
  border-radius: 0;
  padding: 0.5rem 1.25rem;
  font-weight: 500;
  border: none;
  transition: background 0.2s, color 0.2s;
  cursor: pointer;
}
.header-tab:hover:not(.header-tab-selected) {
  background: var(--store-tab-hover-bg) !important;
  color: var(--store-tab-hover-text) !important;
}
.header-tab-selected {
  background: var(--store-tab-selected-bg) !important;
  color: var(--store-tab-selected-text) !important;
}

/* Ensure icon color matches iconColor for country, state, location, and chevron icons in unselected, not-hovered state */
.side-panel-tree-view .location-selector-item:not(.location-selected):not(:hover) .v-icon.mdi-earth,
.side-panel-tree-view .location-selector-item:not(.location-selected):not(:hover) .v-icon.mdi-map,
.side-panel-tree-view .location-selector-item:not(.location-selected):not(:hover) .v-icon.mdi-store,
.side-panel-tree-view .location-selector-item:not(.location-selected):not(:hover) .chevron-icon {
  color: var(--sidepanel-icon) !important;
}

.details-panel {
  box-shadow: 0 6px 24px 0 rgba(80, 80, 120, 0.12);
  padding: 2rem;
}
.details-address {
  background: var(--details-address-bg) !important;
  color: var(--details-address-text) !important;
  border: var(--details-address-border-size) solid var(--details-address-border) !important;
  border-radius: var(--details-address-radius) !important;
}
.details-contact {
  background: var(--details-contact-bg) !important;
  color: var(--details-contact-text) !important;
  border: var(--details-contact-border-size) solid var(--details-contact-border) !important;
  border-radius: var(--details-contact-radius) !important;
}
.details-description {
  background: var(--details-description-bg) !important;
  color: var(--details-description-text) !important;
  border: var(--details-description-border-size) solid var(--details-description-border) !important;
  border-radius: var(--details-description-radius) !important;
}
.details-cover-photo {
  background: var(--details-cover-photo-bg) !important;
  color: var(--details-cover-photo-text) !important;
  border: var(--details-cover-photo-border-size) solid var(--details-cover-photo-border) !important;
  border-radius: var(--details-cover-photo-radius) !important;
}
.details-social-links {
  background: var(--details-social-links-bg) !important;
  color: var(--details-social-links-text) !important;
  border: var(--details-social-links-border-size) solid var(--details-social-links-border) !important;
  border-radius: var(--details-social-links-radius) !important;
}
.details-hours {
  background: var(--details-hours-bg) !important;
  color: var(--details-hours-text) !important;
  border: var(--details-hours-border-size) solid var(--details-hours-border) !important;
  border-radius: var(--details-hours-radius) !important;
}
.details-categories {
  background: var(--details-categories-bg) !important;
  color: var(--details-categories-text) !important;
  border: var(--details-categories-border-size) solid var(--details-categories-border) !important;
  border-radius: var(--details-categories-radius) !important;
}
.details-posts {
  background: var(--details-posts-bg) !important;
  color: var(--details-posts-text) !important;
  border: var(--details-posts-border-size) solid var(--details-posts-border) !important;
  border-radius: var(--details-posts-radius) !important;
}
.details-reviews {
  background: var(--details-reviews-bg) !important;
  color: var(--details-reviews-text) !important;
  border: var(--details-reviews-border-size) solid var(--details-reviews-border) !important;
  border-radius: var(--details-reviews-radius) !important;
}
.details-actions {
  background: var(--details-actions-bg) !important;
  color: var(--details-actions-text) !important;
  border: var(--details-actions-border-size) solid var(--details-actions-border) !important;
  border-radius: var(--details-actions-radius) !important;
}
.details-btn {
  color: var(--details-btn-text) !important;
  background: var(--details-btn-bg) !important;
  border: var(--details-btn-border-size) solid var(--details-btn-border) !important;
  border-radius: var(--details-btn-radius) !important;
  transition: background 0.2s, color 0.2s, border 0.2s;
}
.details-btn .v-icon {
  color: var(--details-btn-text) !important;
}
.details-btn:hover:not(.selected) {
  color: var(--details-btn-hover-text) !important;
  background: var(--details-btn-hover-bg) !important;
  border: var(--details-btn-hover-border-size) solid var(--details-btn-hover-border) !important;
  border-radius: var(--details-btn-hover-border-radius) !important;
}
.details-btn:hover:not(.selected) .v-icon {
  color: var(--details-btn-hover-text) !important;
}
.details-btn.selected {
  color: var(--details-btn-selected-text) !important;
  background: var(--details-btn-selected-bg) !important;
  border: var(--details-btn-selected-border-size) solid var(--details-btn-selected-border) !important;
  border-radius: var(--details-btn-selected-border-radius) !important;
}
.details-btn.selected .v-icon {
  color: var(--details-btn-selected-text) !important;
}

.details-header {
  background: var(--details-header-bg) !important;
  color: var(--details-header-text) !important;
  border: var(--details-header-border-size) solid var(--details-header-border) !important;
  border-radius: var(--details-header-radius) !important;
}

.details-header-icon {
  color: var(--details-primary) !important;
}

.category-badge {
  background: var(--details-primary) !important;
  color: var(--details-header-text) !important;
}
.store-footer {
  border-top: 6px solid var(--footer-primary-color);
  padding: 18px 0 10px 0;
  background: #fff;
  text-align: center;
  font-size: 18px;
  font-family: serif;
  margin-top: 0;
  color: var(--footer-primary-color);
}
.footer-links {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 0.5em;
  flex-direction: row;
}
.footer-link {
  color: var(--footer-primary-color);
  text-decoration: none;
  margin: 0 2px;
  white-space: nowrap;
  cursor: pointer;
}
.footer-link:hover {
  text-decoration: underline;
  color: var(--footer-primary-color);
}
.footer-separator {
  color: var(--footer-primary-color);
}
.city-filter-label {
  color: #000;
  letter-spacing: 0.01em;
}
.city-filter-dropdown {
  border: 2px solid #000;
  background: #fff;
  color: #000;
  font-weight: 500;
  font-size: 1rem;
  transition: border-color 0.2s, box-shadow 0.2s;
  box-shadow: 0 1px 4px 0 rgba(23, 115, 234, 0.05);
}
.city-filter-dropdown:focus {
  border-color: #000;
  box-shadow: 0 0 0 2px color-mix(in srgb, #000 30%, transparent);
}
.city-filter-dropdown option {
  color: #222;
}

.city-filter-container {

  background: #fff;
  border-radius: 0.75rem;
  box-shadow: 0 2px 8px 0 rgba(80, 80, 120, 0.08);
  padding-top: 1.25rem;
  z-index: 2;
  position: relative;
  width: 100%;
}

.status-badge {
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.status-badge-deleted {
  background-color: #fee2e2;
  color: #dc2626;
  border: 1px solid #fecaca;
}
.status-badge-draft {
  background-color: #fef3c7;
  color: #d97706;
  border: 1px solid #fed7aa;
}
.status-badge-scheduled {
  background-color: #dbeafe;
  color: #2563eb;
  border: 1px solid #bfdbfe;
}
.status-badge-published {
  background-color: #dcfce7;
  color: #16a34a;
  border: 1px solid #bbf7d0;
}

/* Cover Photo Styles */
.cover-photo-container {
  position: relative;
  overflow: hidden;
  border-radius: 0.75rem;
}

.cover-photo-container img {
  transition: transform 0.3s ease;
}

.cover-photo-container:hover img {
  transform: scale(1.02);
}

/* Social Links Styles */
.details-social-links a {
  text-decoration: none;
  transition: all 0.2s ease;
}

.details-social-links a:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.details-social-links .v-icon {
  flex-shrink: 0;
}

/* Pagination Styles */
.details-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: #e5e7eb !important;
  color: #6b7280 !important;
  border-color: #d1d5db !important;
}

.details-btn:disabled:hover {
  background: #e5e7eb !important;
  color: #6b7280 !important;
  border-color: #d1d5db !important;
}

.details-btn:disabled .v-icon {
  color: #6b7280 !important;
}

/* Logo in details header card */
.details-header-logo {
  max-width: 150px;
  max-height: 150px;
  border-radius: 0.75rem;
  box-shadow: 0 2px 8px 0 rgba(80, 80, 120, 0.10);
  background: #fff;
}

.brand-name-text {
  font-size: var(--store-brand-name-size) !important;
  color: var(--store-brand-name-color) !important;
}

/* Review Styles */
.review-item {
  background: rgba(255, 255, 255, 0.8) !important;
  border: 1px solid rgba(23, 115, 234, 0.1) !important;
  border-radius: 0.75rem !important;
  padding: 1rem !important;
  box-shadow: 0 2px 8px 0 rgba(80, 80, 120, 0.08) !important;
  transition: all 0.2s ease !important;
}

.review-item:hover {
  box-shadow: 0 4px 12px 0 rgba(80, 80, 120, 0.12) !important;
  transform: translateY(-1px) !important;
}

.review-author {
  font-weight: 600 !important;
  color: var(--details-reviews-text) !important;
  font-size: 0.875rem !important;
}

.review-date {
  font-size: 0.75rem !important;
  color: var(--details-secondary) !important;
}

.review-comment {
  color: var(--details-reviews-text) !important;
  font-size: 0.875rem !important;
  line-height: 1.5 !important;
}

.pagination-container {
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  margin-top: 1.5rem !important;
  padding-top: 1rem !important;
  border-top: 1px solid rgba(23, 115, 234, 0.1) !important;
}

.pagination-info {
  font-size: 0.875rem !important;
  color: var(--details-secondary) !important;
}

.pagination-page-info {
  font-size: 0.875rem !important;
  color: var(--details-secondary) !important;
  padding: 0 0.5rem !important;
}

/* Skeleton Loader Styles */
@keyframes skeleton-pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

@keyframes skeleton-shimmer {
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
}

.skeleton-label {
  height: 16px;
  width: 80px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-dropdown {
  height: 40px;
  width: 100%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 6px;
}

.skeleton-tree-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-radius: 8px;
}

.skeleton-chevron {
  width: 16px;
  height: 16px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 2px;
}

.skeleton-icon {
  width: 16px;
  height: 16px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 50%;
}

.skeleton-text {
  height: 16px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-count {
  width: 40px;
  height: 16px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-location-item {
  height: 60px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 12px;
  margin-bottom: 8px;
}

.skeleton-cover-photo {
  height: 256px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 12px;
}

.skeleton-details-header {
  height: 120px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 12px;
}

.skeleton-panel {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px 0 rgba(80, 80, 120, 0.08);
}

.skeleton-panel-header {
  height: 24px;
  width: 200px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 4px;
  margin-bottom: 16px;
}

.skeleton-panel-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.skeleton-text-long {
  height: 20px;
  width: 100%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-text-medium {
  height: 16px;
  width: 70%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-button {
  height: 40px;
  width: 140px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 8px;
}

.skeleton-map {
  height: 192px;
  width: 100%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 12px;
}

.skeleton-contact-item {
  height: 40px;
  width: 100%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 8px;
}

.skeleton-review-item {
  height: 80px;
  width: 100%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 8px;
}

/* Header Skeleton Styles */
.skeleton-logo {
  width: 48px;
  height: 48px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 8px;
}

.skeleton-brand-name {
  height: 24px;
  width: 120px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-location-count {
  height: 16px;
  width: 80px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-tab {
  height: 36px;
  width: 80px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 6px;
}

.skeleton-search {
  height: 40px;
  width: 320px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 8px;
}

/* Responsive skeleton adjustments */
@media (max-width: 1024px) {
  .skeleton-panel {
    padding: 16px;
  }
  
  .skeleton-cover-photo {
    height: 200px;
  }
  
  .skeleton-details-header {
    height: 100px;
  }
}

@media (max-width: 768px) {
  .skeleton-panel {
    padding: 12px;
  }
  
  .skeleton-cover-photo {
    height: 180px;
  }
  
  .skeleton-details-header {
    height: 80px;
  }
  
  .skeleton-map {
    height: 160px;
  }
  
  .skeleton-search {
    width: 200px;
  }
  
  .skeleton-brand-name {
    width: 80px;
  }
  
  .skeleton-location-count {
    width: 60px;
  }
  
  .skeleton-tab {
    width: 60px;
  }
}
</style> 